import { useState, useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { AIAgent, AttachedMedia } from "@/types/aiAgents";
import { SearchResult } from "@/types/searchResults";
import {
  aiChatService,
  imageGenerationService,
  visionChatService,
  visionChatServiceMulti,
  lastGeminiReasoning,
  upstageAiService,
  enrichPromptForFileCreation,
} from "@/services/supabaseServices";
import { analyzeWithNova } from "@/services/openrouterNova";
import { callQwenAI } from "@/services/qwenService";
import { WikipediaCard } from "./agents/WikipediaCard";
import { SourceCarousel } from "./search/SourceCarousel";
import { getFaviconFromUrl } from "@/utils/favicon";
import { fetchWeather } from "@/services/agents/weatherAgent";
import {
  wikipediaService
} from "@/services/agents/wikipediaAgent";
import {
  giphyService,
  formatGiphyResponse,
} from "@/services/agents/giphyAgent";
import { recipeService } from "@/services/agents/recipeAgent";
import {
  youtubeService,
} from "@/services/agents/youtubeAgent";
import {
  evaluateMathExpression,
  formatMathResponse,
} from "@/services/agents/mathAgent";
import {
  generateQRCodeURL,
  formatQRCodeData,
} from "@/services/agents/qrCodeAgent";
import { generateColorPaletteWithAI } from "@/services/agents/colorPaletteAgent";
import {
  pixabayService,
  formatPixabayResponse,
} from "@/services/agents/pixabayAgent";
import {
  wikiImagesService,
  formatWikiImagesResponse,
} from "@/services/agents/wikiImagesAgent";
import {
  videoSummaryService,
  formatVideoSummaryResponse,
} from "@/services/agents/videoSummaryAgent";
import { generatePPTWithGroq } from "@/services/agents/pptMakerAgent";
import { supabase } from "@/integrations/supabase/client";
import {
  generateMemeWithAI,
  showMemeTemplates as showMemeTemplatesService,
} from "@/services/agents/memeAgent";
import { getAIResponse } from "@/utils/aiResponses";
import { searchWeb, searchWebWithStreaming, formatSearchResults } from "@/services/tavilyService";
import { WeatherData } from "./WeatherCard";
import { deepResearchRespond } from "@/services/parallelService";
import { useTempChat } from "@/contexts/TempChatContext";
import { convertFileToImages } from "@/services/fileImageConverter";
import {
  gptOssCodeExplain,
  gptOssCalculatorWithExplanation,
  gptOssTranslatorWithExplanation,
} from "@/services/agents/gptOssAgentService";
import LARAOrchestrator from "@/services/agenticAI/AgenticAIOrchestrator";
import { getUserFacts } from "@/services/userFactsService";
import { buildFlowChart } from "@/services/agents/flowAgent";

interface Message {
  id: number;
  content: string;
  type: "user" | "ai";
  timestamp: Date;
  model?: string;
  displayModel?: string;
  selectedAgent?: string;
  attachedMedia?: AttachedMedia;
  attachedMediaList?: AttachedMedia[];
  isImage?: boolean;
  isStreaming?: boolean;
  reasoning?: string;
  reasoning_content?: string;
  agentData?: any;
  weatherData?: WeatherData;
  laraResults?: any[];
  isLARA?: boolean;
  searchResults?: SearchResult;
  parallelSources?: any[];
  multiBatchId?: string;
  multiOrder?: number;
  modelAlias?: string;
}

interface MessageHandlerProps {
  inputValue: string;
  selectedModel: string;
  isImageMode: boolean;
  attachedMedia: AttachedMedia | null;
  attachments?: AttachedMedia[];
  isQuizMode?: boolean;
  precomputedFallbackCollage?: {
    file?: File;
    dataUrl?: string;
  } | null;
  selectedAgent: AIAgent | null;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  setInputValue: (value: string) => void;
  setIsLoading: (loading: boolean) => void;
  setAttachedMedia: (media: AttachedMedia | null) => void;
  setSelectedMemeTemplate: (template: any) => void;
  setMemeTemplates: (templates: any[]) => void;
  setShowMemeTemplates: (show: boolean) => void;
  setGifs: (gifs: any[]) => void;
  setShowGifs: (show: boolean) => void;
  selectedMemeTemplate: any;
  showMemeTemplates: boolean;
  memeTemplates: any[];
  user: any;
  incrementMessageCount: () => void;
  isDarkMode?: boolean;
  isWebSearchMode?: boolean;
  isDeepResearchMode?: boolean;
  saveMessageToSupabase?: (message: any) => Promise<boolean>;
  currentChatId?: string | null;
  // Multi-AI
  isMultiAIMode?: boolean;
  selectedMultiAIModels?: string[];
}

// Counter to ensure unique message IDs
let messageIdCounter = 0;
const generateUniqueMessageId = () => {
  const timestamp = Date.now();
  messageIdCounter++;
  return timestamp + messageIdCounter;
};



export const useMessageHandler = (props: MessageHandlerProps) => {
  const {
    inputValue,
    selectedModel,
    isImageMode,
    isWebSearchMode = false,
    isDeepResearchMode = false,
    isQuizMode = false,
    attachedMedia,
    attachments = [],
    precomputedFallbackCollage = null,
    selectedAgent,
    setMessages,
    setInputValue,
    setIsLoading,
    setAttachedMedia,
    setSelectedMemeTemplate,
    setMemeTemplates,
    setShowMemeTemplates,
    setGifs,
    setShowGifs,
    selectedMemeTemplate,
    showMemeTemplates,
    memeTemplates,
    user,
    incrementMessageCount,
    isDarkMode,
    saveMessageToSupabase,
    currentChatId,
    isMultiAIMode = false,
    selectedMultiAIModels = [],
  } = props;

  const { toast } = useToast();
  const userFactsSummaryRef = useRef<string>("");

  const quizSystemPrompt = useRef<string>(
    'GOAL\nCreate an interactive quiz for the user on the requested topic. The quiz must be solvable and engaging, similar to ChatGPT\u2019s quiz style.\n\nQUESTION COUNT RULES\n- Default: 5 questions unless the user requests a specific number.\n- Minimum: 3 questions.\n- Maximum: 20 questions.\n- If the user requests more than 20, cap at 20.\n- If the user requests fewer than 3, raise to 3.\n\nQUESTION TYPES & OPTIONS\n- Each question MUST be either:\n  (A) Multiple choice with 3 or 4 options, OR\n  (B) True/False.\n- Use True/False only when it genuinely fits; otherwise prefer multiple choice.\n- Exactly ONE correct answer per question.\n- Options must be plausible and not trivially eliminable.\n- Avoid ambiguous wording; keep one best answer.\n\nDIFFICULTY & CONTENT\n- Match difficulty to the user\u2019s level if stated; otherwise start easy-to-medium and gently ramp up.\n- Keep questions concise.\n- Do not include any disallowed content.\n- Do not ask for personal data.\n\nOUTPUT FORMAT (STRICT JSON ONLY)\nReturn ONLY valid JSON (no markdown, no comments, no trailing commas), matching:\n\n{\n  "mode": "quiz",\n  "title": string,\n  "topic": string,\n  "numQuestions": number,\n  "questions": [\n    {\n      "id": string,\n      "type": "mcq" | "boolean",\n      "question": string,\n      "options": string[],\n      "correctIndex": number,\n      "explanation": string\n    }\n  ]\n}\n\nIMPORTANT BEHAVIOR RULES\n- Do NOT include any UI text, instructions, or extra fields outside the schema.\n- Do NOT include score, user answers, or results calculations.\n- The client app will handle interaction, scoring, and showing results.\n- Keep IDs stable within one response.\n- Ensure correctIndex is always in range.'
  );

  useEffect(() => {
    getUserFacts().then(data => {
      userFactsSummaryRef.current = data.summary || "";
    }).catch(() => { /* ignore */ });
  }, []);

  const createCollageFromImages = async (files: File[]) => {
    const imgs: HTMLImageElement[] = [];
    for (const f of files) {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result as string);
        fr.onerror = reject;
        fr.readAsDataURL(f);
      });
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = dataUrl;
      });
      imgs.push(img);
    }
    const n = Math.min(4, imgs.length);
    const gap = 16;
    const radius = 20;
    const maxCanvas = 1600;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
    if (!ctx) throw new Error('Canvas unavailable');
    const frames: { x: number; y: number; w: number; h: number }[] = [];
    if (n === 1) {
      const iw = imgs[0].width;
      const ih = imgs[0].height;
      let W = Math.min(maxCanvas, Math.max(320, iw));
      let H = Math.min(maxCanvas, Math.max(320, ih));
      const scale = Math.min(maxCanvas / Math.max(W + 2 * gap, 1), maxCanvas / Math.max(H + 2 * gap, 1));
      W = Math.round(W * scale);
      H = Math.round(H * scale);
      canvas.width = W + 2 * gap;
      canvas.height = H + 2 * gap;
      frames.push({ x: gap, y: gap, w: W, h: H });
    } else if (n === 2) {
      const ar1 = imgs[0].width / imgs[0].height;
      const ar2 = imgs[1].width / imgs[1].height;
      let H = Math.min(maxCanvas - 2 * gap, 900);
      let w1 = H * ar1;
      let w2 = H * ar2;
      let W = Math.round(w1 + w2 + 3 * gap);
      if (W > maxCanvas) {
        const s = maxCanvas / W;
        W = Math.round(W * s);
        H = Math.round(H * s);
        w1 = Math.round(w1 * s);
        w2 = Math.round(w2 * s);
      }
      canvas.width = W;
      canvas.height = H + 2 * gap;
      frames.push({ x: gap, y: gap, w: Math.round(w1), h: H });
      frames.push({ x: Math.round(2 * gap + w1), y: gap, w: Math.round(w2), h: H });
    } else {
      let cell = Math.min(Math.floor((maxCanvas - 3 * gap) / 2), 720);
      canvas.width = 2 * cell + 3 * gap;
      canvas.height = 2 * cell + 3 * gap;
      const w = cell;
      const h = cell;
      frames.push({ x: gap, y: gap, w, h });
      frames.push({ x: 2 * gap + w, y: gap, w, h });
      frames.push({ x: gap, y: 2 * gap + h, w, h });
      frames.push({ x: 2 * gap + w, y: 2 * gap + h, w, h });
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const roundRectPath = (x: number, y: number, w: number, h: number, r: number) => {
      const rr = Math.min(r, w / 2, h / 2);
      const p = new Path2D();
      p.moveTo(x + rr, y);
      p.lineTo(x + w - rr, y);
      p.quadraticCurveTo(x + w, y, x + w, y + rr);
      p.lineTo(x + w, y + h - rr);
      p.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
      p.lineTo(x + rr, y + h);
      p.quadraticCurveTo(x, y + h, x, y + h - rr);
      p.lineTo(x, y + rr);
      p.quadraticCurveTo(x, y, x + rr, y);
      p.closePath();
      return p;
    };
    const drawContain = (image: HTMLImageElement, fx: number, fy: number, fw: number, fh: number) => {
      const iw = image.width;
      const ih = image.height;
      const scale = Math.min(fw / iw, fh / ih);
      const dw = Math.max(1, Math.floor(iw * scale));
      const dh = Math.max(1, Math.floor(ih * scale));
      const dx = Math.floor(fx + (fw - dw) / 2);
      const dy = Math.floor(fy + (fh - dh) / 2);
      const path = roundRectPath(fx, fy, fw, fh, radius);
      ctx.save();
      ctx.clip(path);
      ctx.drawImage(image, dx, dy, dw, dh);
      ctx.restore();
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 4;
      ctx.stroke(path);
    };
    if (n === 3) {
      const order = [0, 1, 2];
      for (let i = 0; i < order.length; i++) {
        const f = frames[i];
        drawContain(imgs[order[i]], f.x, f.y, f.w, f.h);
      }
    } else if (n === 4) {
      for (let i = 0; i < 4; i++) {
        const f = frames[i];
        drawContain(imgs[i], f.x, f.y, f.w, f.h);
      }
    } else if (n === 2) {
      for (let i = 0; i < 2; i++) {
        const f = frames[i];
        drawContain(imgs[i], f.x, f.y, f.w, f.h);
      }
    } else if (n === 1) {
      const f = frames[0];
      drawContain(imgs[0], f.x, f.y, f.w, f.h);
    }
    const dataUrl: string = await new Promise((resolve) => canvas.toBlob((b) => resolve(URL.createObjectURL(b!)), 'image/png'));
    const blob: Blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/png'));
    const file = new File([blob], 'collage.png', { type: 'image/png' });
    const dataUrlInline: string = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(file);
    });
    return { dataUrl: dataUrlInline, file };
  };

  const createLabeledCollageFromImages = async (files: File[], labelText: string) => {
    const imgs: HTMLImageElement[] = [];
    for (const f of files) {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result as string);
        fr.onerror = reject;
        fr.readAsDataURL(f);
      });
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = reject;
        el.src = dataUrl;
      });
      imgs.push(img);
    }

    const n = Math.min(3, imgs.length);
    const gap = 16;
    const radius = 20;
    const maxCanvas = 1600;
    const cell = Math.min(Math.floor((maxCanvas - 3 * gap) / 2), 720);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D | null;
    if (!ctx) throw new Error('Canvas unavailable');

    canvas.width = 2 * cell + 3 * gap;
    canvas.height = 2 * cell + 3 * gap;

    const frames = [
      { x: gap, y: gap, w: cell, h: cell },
      { x: 2 * gap + cell, y: gap, w: cell, h: cell },
      { x: gap, y: 2 * gap + cell, w: cell, h: cell },
      { x: 2 * gap + cell, y: 2 * gap + cell, w: cell, h: cell },
    ];

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const roundRectPath = (x: number, y: number, w: number, h: number, r: number) => {
      const rr = Math.min(r, w / 2, h / 2);
      const p = new Path2D();
      p.moveTo(x + rr, y);
      p.lineTo(x + w - rr, y);
      p.quadraticCurveTo(x + w, y, x + w, y + rr);
      p.lineTo(x + w, y + h - rr);
      p.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
      p.lineTo(x + rr, y + h);
      p.quadraticCurveTo(x, y + h, x, y + h - rr);
      p.lineTo(x, y + rr);
      p.quadraticCurveTo(x, y, x + rr, y);
      p.closePath();
      return p;
    };

    const drawContain = (image: HTMLImageElement, fx: number, fy: number, fw: number, fh: number) => {
      const iw = image.width;
      const ih = image.height;
      const scale = Math.min(fw / iw, fh / ih);
      const dw = Math.max(1, Math.floor(iw * scale));
      const dh = Math.max(1, Math.floor(ih * scale));
      const dx = Math.floor(fx + (fw - dw) / 2);
      const dy = Math.floor(fy + (fh - dh) / 2);
      const path = roundRectPath(fx, fy, fw, fh, radius);
      ctx.save();
      ctx.clip(path);
      ctx.drawImage(image, dx, dy, dw, dh);
      ctx.restore();
      ctx.strokeStyle = '#e5e7eb';
      ctx.lineWidth = 4;
      ctx.stroke(path);
    };

    for (let i = 0; i < n; i++) {
      const f = frames[i];
      drawContain(imgs[i], f.x, f.y, f.w, f.h);
    }

    const tf = frames[3];
    const textPath = roundRectPath(tf.x, tf.y, tf.w, tf.h, radius);
    ctx.save();
    ctx.clip(textPath);
    ctx.fillStyle = '#f9fafb';
    ctx.fillRect(tf.x, tf.y, tf.w, tf.h);
    ctx.restore();
    ctx.strokeStyle = '#e5e7eb';
    ctx.lineWidth = 4;
    ctx.stroke(textPath);

    ctx.fillStyle = '#111827';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const fontSize = Math.max(18, Math.min(34, Math.floor(tf.w / 18)));
    ctx.font = `600 ${fontSize}px ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial`;

    const lines = String(labelText || '').split(/\n+/g).map((s) => s.trim()).filter(Boolean);
    const safeLines = lines.length ? lines.slice(0, 4) : ['It\'s a collage of 3 different separate images'];
    const lineHeight = Math.round(fontSize * 1.35);
    const totalH = safeLines.length * lineHeight;
    let y = tf.y + tf.h / 2 - totalH / 2 + lineHeight / 2;
    for (const line of safeLines) {
      ctx.fillText(line, tf.x + tf.w / 2, y);
      y += lineHeight;
    }

    const blob: Blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b!), 'image/png'));
    const file = new File([blob], 'collage.png', { type: 'image/png' });
    const dataUrlInline: string = await new Promise((resolve) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result as string);
      r.readAsDataURL(file);
    });
    return { dataUrl: dataUrlInline, file };
  };

  const { isTempMode } = useTempChat();

  const urlToDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const uploadAttachmentToCloudinary = async (file: File): Promise<string> => {
    const fileToDataUrl = (f: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
    const imageBase64 = await fileToDataUrl(file);
    const res = await supabase.functions.invoke("ChatAttachedImage", {
      body: {
        action: "ATTACHED_UPLOAD",
        imageBase64,
        filename: file.name,
        mime: file.type,
      },
    });
    if ((res as any).error) throw new Error((res as any).error.message || 'Upload failed');
    const data = (res as any).data || {};
    if (!data?.imageUrl) throw new Error('Invalid upload response');
    return data.imageUrl as string;
  };

  const uploadAttachmentToPrivateStorage = async (file: File): Promise<{ imageUrl: string; publicId?: string }> => {
    const fileToDataUrl = (f: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(f);
      });
    const imageBase64 = await fileToDataUrl(file);
    const { data: { user: currentUser } } = await supabase.auth.getUser();
    const res = await supabase.functions.invoke("ChatAttachedImage", {
      body: {
        action: "ATTACHED_UPLOAD",
        imageBase64,
        filename: file.name,
        mime: file.type,
        userId: currentUser?.id || null,
      },
    });
    if ((res as any).error) throw new Error((res as any).error.message || 'Upload failed');
    const data = (res as any).data || {};
    if (!data?.imageUrl) throw new Error('Invalid upload response');
    return { imageUrl: data.imageUrl, publicId: data.publicId };
  };

  const handleSend = async () => {
    // Will hold structured sources (e.g., Perplexity search results) for the final AI message
    let finalSearchResults: SearchResult | undefined;
    if (!inputValue.trim() && !attachedMedia && !isImageMode && !selectedAgent) return;

    localStorage.setItem("currentQuery", inputValue.trim());

    // Do not show AI/generating loader until uploads (if any) have started/completed.
    // ChatInputArea listens to attachmentUpload:* events for upload UI.
    const hasAnyAttachments = Array.isArray(attachments) && attachments.length > 0;
    if (!hasAnyAttachments) {
      setIsLoading(true);
    }
    incrementMessageCount();

    const isLARARequest = selectedModel === "lara";

    // Handle LARA multi-agent requests first
    if (isLARARequest && !isImageMode && !attachedMedia) {
      try {
        // Add user message
        const userMessage: Message = {
          id: generateUniqueMessageId(),
          content: inputValue,
          type: "user",
          timestamp: new Date(),
          model: "lara",
          selectedAgent: selectedAgent?.id,
        };

        setMessages((prev) => [...prev, userMessage]);
        // Save user message immediately to Supabase
        if (saveMessageToSupabase) {
          saveMessageToSupabase(userMessage);
        }
        setInputValue("");
        setAttachedMedia(null);

        // Show initial LARA loading message (empty content - loader will show the status)
        const laraLoadingId = generateUniqueMessageId();
        const laraLoadingMessage: Message = {
          id: laraLoadingId,
          content: "", // Empty - loader will show dynamic messages
          type: "ai",
          timestamp: new Date(),
          model: "lara",
          isStreaming: true,
          isLARA: true,
        };
        setMessages((prev) => [...prev, laraLoadingMessage]);

        // Update loading message - only update localStorage for loader, not message content
        const updateLoadingMessage = (status: string) => {
          localStorage.setItem("currentQuery", status);
          // Don't update message content - loader will show the status
        };

        // Get LARA orchestrator instance
        const lara = LARAOrchestrator.getInstance();

        // Analyze the request
        updateLoadingMessage("LARA is planning your tasks...");
        const response = await lara.analyzeRequest(inputValue);

        // Check if this is a chatbot response (no specific tasks)
        if (
          response.mode === "chatbot_response" ||
          response.tasks.length === 0
        ) {
          // Handle as general AI chatbot response
          setMessages((prev) => {
            const updated = [...prev];
            const loadingMsgIndex = updated.findIndex(
              (msg) => msg.id === laraLoadingId
            );
            if (loadingMsgIndex !== -1) {
              updated[loadingMsgIndex] = {
                ...updated[loadingMsgIndex],
                content:
                  response.response ||
                  "I'm LARA, your AI assistant. How can I help you today?",
                isStreaming: false,
                isLARA: true,
              };
            }
            return updated;
          });
          setIsLoading(false);
          return;
        }

        // Define task tracking for progressive execution
        const totalTasks = response.tasks.length;
        const taskStatuses = response.tasks.map((task: any) => ({
          name: task.displayName || task.agentName,
          status: "pending" as "pending" | "executing" | "completed" | "failed",
        }));

        const updateTaskOverview = () => {
          // Update localStorage for loader to show task overview
          localStorage.setItem("currentQuery", `Executing ${totalTasks} tasks...`);
          // Don't update message content - loader will show the status
        };

        const results: any[] = [];
        let completedTasks = 0;

        // Execute tasks and collect results (no individual messages)
        for (let i = 0; i < response.tasks.length; i++) {
          const task = response.tasks[i];
          taskStatuses[i].status = "executing";
          updateTaskOverview();

          try {
            const completedTask = await lara.executeTask(task, (status) => {
              // Update individual task status in loader
              const taskName = task.displayName || task.agentName;
              updateLoadingMessage(`Executing ${taskName}`);
            });

            completedTasks++;

            if (completedTask.status === "completed" && completedTask.result) {
              taskStatuses[i].status = "completed";
              const formattedResult = lara.formatTaskResult(completedTask);
              results.push(formattedResult);
            } else {
              taskStatuses[i].status = "failed";
            }
          } catch (error) {
            console.error(`Task ${task.agentName} failed:`, error);
            taskStatuses[i].status = "failed";
            completedTasks++;
          }

          // Update the overview with current progress
          updateTaskOverview();
        }

        // Create single consolidated results message with all completed tasks
        if (results.length > 0) {
          const consolidatedResultsMessage: Message = {
            id: generateUniqueMessageId(), // Unique ID for consolidated results
            content: "",
            type: "ai",
            timestamp: new Date(),
            model: "lara",
            isStreaming: false,
            isLARA: true,
            laraResults: results, // All results in single message
            agentData: {
              isConsolidatedResults: true,
              totalTasks: totalTasks,
              completedCount: results.length,
              taskNames: response.tasks.map(
                (t) => t.displayName || t.agentName
              ),
            },
          };

          // Add the consolidated results message
          setMessages((prev) => [...prev, consolidatedResultsMessage]);

          // Save consolidated results to Supabase if available
          if (saveMessageToSupabase) {
            saveMessageToSupabase(consolidatedResultsMessage);
          }
        }

        // Final update to the overview message - keep it as task overview
        setMessages((prev) => {
          const updated = [...prev];
          const loadingMsgIndex = updated.findIndex(
            (msg) => msg.id === laraLoadingId
          );
          if (loadingMsgIndex !== -1) {
            updated[loadingMsgIndex] = {
              ...updated[loadingMsgIndex],
              content: ``,
              isStreaming: false,
              isLARA: true,
            };
          }
          return updated;
        });

        // Add execution summary at the bottom as a separate message
        const completedCount = taskStatuses.filter(
          (t) => t.status === "completed"
        ).length;
        const failedCount = taskStatuses.filter(
          (t) => t.status === "failed"
        ).length;
      } catch (error) {
        console.error("LARA error:", error);
        toast({
          title: "LARA Error",
          description:
            "Failed to process multi-agent request. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    const searchPrefixes = ["search for", "find", "look up", "web search"];
    const isSearchQuery =
      isWebSearchMode ||
      searchPrefixes.some((prefix) =>
        inputValue.trim().toLowerCase().startsWith(prefix)
      );

    const quizIntent = (() => {
      const t = (inputValue || '').trim().toLowerCase();
      if (!t) return false;
      if (isQuizMode) return true;
      return (
        t.includes('start quiz') ||
        t.includes('make a quiz') ||
        t.includes('quiz me') ||
        t.includes('test me')
      );
    })();

    if (quizIntent && !isImageMode && !isSearchQuery && !isDeepResearchMode && !attachedMedia) {
      try {
        const userMessage: Message = {
          id: generateUniqueMessageId(),
          content: inputValue,
          type: 'user',
          timestamp: new Date(),
          model: selectedModel,
          selectedAgent: selectedAgent?.id,
        };

        setMessages((prev) => [...prev, userMessage]);
        if (saveMessageToSupabase) {
          saveMessageToSupabase(userMessage);
        }
        setInputValue('');
        setAttachedMedia(null);

        const tempAiId = generateUniqueMessageId();
        const tempAiMessage: Message = {
          id: tempAiId,
          content: '',
          type: 'ai',
          timestamp: new Date(),
          model: 'quiz',
          displayModel: 'quiz',
          isStreaming: true,
        };
        setMessages((prev) => [...prev, tempAiMessage]);

        const { data, error } = await supabase.functions.invoke('quiz', {
          body: {
            message: inputValue,
            temperature: 0.2,
          },
        });

        if (error) {
          throw error;
        }

        const full = ((): string => {
          if (!data) return '';
          if (typeof data === 'string') return data;
          return (data.response || data.content || data.message || '') as string;
        })();

        setMessages((prev) => {
          const next = prev.map((m) => m.id === tempAiId ? { ...m, content: String(full || ''), isStreaming: false } : m);
          const saved = next.find((m) => m.id === tempAiId);
          if (saveMessageToSupabase && saved) {
            saveMessageToSupabase(saved);
          }
          return next;
        });
      } catch (error: any) {
        console.error('Quiz generation failed:', error);
        toast({
          title: 'Quiz Error',
          description: error?.message || 'Failed to generate quiz. Please try again.',
          variant: 'destructive',
        });
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (isSearchQuery) {
      try {
        const userMessage: Message = {
          id: generateUniqueMessageId(),
          content: inputValue,
          type: "user",
          timestamp: new Date(),
          model: selectedModel,
          selectedAgent: selectedAgent?.id,
        };

        setMessages((prev) => [...prev, userMessage]);
        // Save user message immediately to Supabase
        if (saveMessageToSupabase) {
          saveMessageToSupabase(userMessage);
        }
        setInputValue("");
        setAttachedMedia(null);

        // Show loading indicator with animation
        const searchMessageId = generateUniqueMessageId();
        const searchMessage: Message = {
          id: searchMessageId,
          content: "",
          type: "ai",
          timestamp: new Date(),
          model: "web-search",
          selectedAgent: "web-search",
          searchResults: undefined,
          isStreaming: true,
        };
        setMessages((prev) => [...prev, searchMessage]);

        // Perform the web search - use the full input in web search mode, otherwise clean the query
        const query = isWebSearchMode
          ? inputValue.trim()
          : inputValue.replace(
            new RegExp(`^(${searchPrefixes.join("|")})\\s+`, "i"),
            ""
          );

        let accumulatedContent = "";
        let searchMetadata: any = null;

        // Use streaming web search with GPT-OSS
        await searchWebWithStreaming(
          query,
          true,
          // onMetadata
          (metadata) => {
            searchMetadata = metadata;
            // Update message with metadata
            setMessages((prev) => {
              const updated = [...prev];
              const msgIndex = updated.findIndex((msg) => msg.id === searchMessageId);
              if (msgIndex !== -1) {
                updated[msgIndex] = {
                  ...updated[msgIndex],
                  searchResults: {
                    query: metadata.query,
                    answer: metadata.answer || "",
                    searchDepth: metadata.searchDepth,
                    results: metadata.results,
                    images: metadata.images,
                    gptEnhanced: true,
                  },
                };
              }
              return updated;
            });
          },
          // onContent
          (content) => {
            accumulatedContent += content;
            // Update message with streaming content
            setMessages((prev) => {
              const updated = [...prev];
              const msgIndex = updated.findIndex((msg) => msg.id === searchMessageId);
              if (msgIndex !== -1) {
                updated[msgIndex] = {
                  ...updated[msgIndex],
                  content: accumulatedContent,
                  isStreaming: true,
                };
              }
              return updated;
            });
          },
          // onComplete
          (fullContent) => {
            // Finalize the message and inject the answer into searchResults
            setMessages((prev) => {
              const updated = [...prev];
              const msgIndex = updated.findIndex((msg) => msg.id === searchMessageId);
              if (msgIndex !== -1) {
                const existing = updated[msgIndex];
                updated[msgIndex] = {
                  ...existing,
                  content: fullContent,
                  isStreaming: false,
                  searchResults: existing.searchResults
                    ? { ...existing.searchResults, answer: fullContent }
                    : existing.searchResults,
                };
                // Save the final message to Supabase
                if (saveMessageToSupabase) {
                  saveMessageToSupabase(updated[msgIndex]);
                }
              }
              return updated;
            });
            setIsLoading(false);
          },
          // onError
          (error) => {
            console.error("Search error:", error);
            setMessages((prev) => {
              const updated = [...prev];
              const msgIndex = updated.findIndex((msg) => msg.id === searchMessageId);
              if (msgIndex !== -1) {
                updated[msgIndex] = {
                  ...updated[msgIndex],
                  content: "Sorry, I encountered an error while searching. Please try again.",
                  isStreaming: false,
                };
              }
              return updated;
            });
            toast({
              title: "Search Error",
              description: "Failed to perform web search. Please try again.",
              variant: "destructive",
            });
            setIsLoading(false);
          }
        );
      } catch (error) {
        console.error("Search error:", error);
        toast({
          title: "Search Error",
          description: "Failed to perform web search. Please try again.",
          variant: "destructive",
        });
        setIsLoading(false);
      }
      return;
    }

    // Handle Deep Research mode (Parallel chat + optional web search)
    if (isDeepResearchMode) {
      try {
        // Add user message
        const userMessage: Message = {
          id: generateUniqueMessageId(),
          content: inputValue,
          type: "user",
          timestamp: new Date(),
          model: selectedModel,
          selectedAgent: selectedAgent?.id,
        };
        setMessages((prev) => [...prev, userMessage]);
        if (saveMessageToSupabase) {
          saveMessageToSupabase(userMessage);
        }
        setInputValue("");
        setAttachedMedia(null);

        // Temporary AI message for streaming indicator
        const tempAiId = generateUniqueMessageId();
        const tempAiMessage: Message = {
          id: tempAiId,
          content: "",
          type: "ai",
          timestamp: new Date(),
          model: "deep-research",
          isStreaming: true,
        };
        setMessages((prev) => [...prev, tempAiMessage]);

        // Prepare context
        const lastParsedDoc = localStorage.getItem("lastParsedDocument");
        let documentContext = "";
        const memoryContext = ""; // User Facts handled server-side now
        if (lastParsedDoc) {
          try {
            const parsedDoc = JSON.parse(lastParsedDoc);
            if (Date.now() - parsedDoc.timestamp < 3600000) {
              documentContext = `\n\nDocument Context (from ${parsedDoc.filename
                }):\n${parsedDoc.content.substring(0, 2000)}${parsedDoc.content.length > 2000 ? "..." : ""
                }`;
            }
          } catch (e) {
            console.error("Error parsing document context:", e);
          }
        }

        let inputWithContext = inputValue;
        if (documentContext) {
          inputWithContext += documentContext;
        }

        const response = await deepResearchRespond(
          inputWithContext,
          new Date().toISOString(),
          { processor: "base" }
        );

        // Extract sources from the response if available
        let parallelSources = [];
        if (response.sources && Array.isArray(response.sources)) {
          parallelSources = response.sources;
        }

        // Stream the content after generation for better UX
        const fullContent = response.content || "";
        const finalParallelSources = parallelSources;
        const baseDelay = 28;
        const maxChunkLength = 110;

        const finalizeMessage = (contentToUse: string) => {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === tempAiId
                ? {
                  ...m,
                  content: contentToUse,
                  isStreaming: false,
                  parallelSources: finalParallelSources,
                }
                : m
            )
          );

          if (saveMessageToSupabase) {
            const finalMsg: Message = {
              id: tempAiId,
              content: contentToUse,
              type: "ai",
              timestamp: new Date(),
              model: "deep-research",
              parallelSources: finalParallelSources,
            };
            saveMessageToSupabase(finalMsg);
          }

          if (contentToUse) {
            // Memory saving removed
          }
        };

        if (!fullContent) {
          finalizeMessage("");
        } else {
          const shouldChunk = fullContent.length > 600;
          const chunks: string[] = [];

          if (shouldChunk) {
            const sentences = fullContent
              .split(/(?<=[.!?])\s+/)
              .filter(Boolean);

            let currentChunk = "";
            sentences.forEach((sentence) => {
              const tentative = currentChunk
                ? `${currentChunk} ${sentence}`
                : sentence;
              if (tentative.length > maxChunkLength) {
                if (currentChunk) {
                  chunks.push(currentChunk);
                }
                currentChunk = sentence;
              } else {
                currentChunk = tentative;
              }
            });

            if (currentChunk) {
              chunks.push(currentChunk);
            }

            if (chunks.length <= 1) {
              const fallbackChunks: string[] = [];
              for (let i = 0; i < fullContent.length; i += maxChunkLength) {
                fallbackChunks.push(fullContent.slice(i, i + maxChunkLength));
              }
              chunks.splice(0, chunks.length, ...fallbackChunks);
            }
          } else {
            chunks.push(fullContent);
          }

          if (chunks.length <= 1) {
            finalizeMessage(fullContent);
          } else {
            let buffer = "";
            let chunkIndex = 0;

            const streamNextChunk = () => {
              if (chunkIndex >= chunks.length) {
                finalizeMessage(fullContent);
                return;
              }

              buffer = buffer
                ? `${buffer} ${chunks[chunkIndex]}`
                : chunks[chunkIndex];
              chunkIndex += 1;

              setMessages((prev) =>
                prev.map((m) =>
                  m.id === tempAiId
                    ? {
                      ...m,
                      content: buffer,
                      isStreaming: true,
                      parallelSources: finalParallelSources,
                    }
                    : m
                )
              );

              const chunkDelay = Math.min(
                shouldChunk ? baseDelay * (1 + buffer.length / 500) : baseDelay,
                200
              );

              setTimeout(streamNextChunk, chunkDelay);
            };

            streamNextChunk();
          }
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Multi-AI parallel dispatch (non-image, non-search, non-LARA, non-deep-research)
    // Only trigger when 2 or more models are selected; if only one is selected, fall back to normal single-model flow
    if (!isImageMode && !attachedMedia && isMultiAIMode && selectedMultiAIModels.length > 1) {
      try {
        // Add user message once
        const userMessage: Message = {
          id: generateUniqueMessageId(),
          content: inputValue,
          type: "user",
          timestamp: new Date(),
          model: selectedModel,
          selectedAgent: selectedAgent?.id,
        };
        setMessages((prev) => [...prev, userMessage]);
        // Note: We DO NOT save the user message yet. We wait until all AI responses are generated
        // to avoid race conditions and ensure correct ordering (User first, then AIs).

        setInputValue("");
        setAttachedMedia(null);

        const batchId = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
          ? (crypto as any).randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

        // Mapping from UI label to service model id
        const mapUIToService = (m: string): string => {
          const key = (m || "").toLowerCase();
          if (key === "chat gpt" || key === "chatgpt" || key === "gpt") return "glm";
          if (key === "solar pro") return "upstage";
          if (key === "gpt 5") return "gpt5";
          return key; // deepseek, gemini, qwen, llama, sambanova, cerebras
        };

        // Stable UI order as requested
        const uiOrder = [
          'chatgpt',
          'gemini',
          'deepseek',
          'google ai',
          'perplexity',
          'kimi',
          'qwen',
          'llama',
          'sambanova',
          'cerebras',
          'solar pro',
          'gpt 5',
        ];

        // Apply one-shot "chat with only this AI next" override if set
        let forcedService: string | null = null;
        try { forcedService = localStorage.getItem('chatWithOnlyNext'); } catch { }
        if (forcedService) {
          try { localStorage.removeItem('chatWithOnlyNext'); } catch { }
        }

        // Create temp AI messages per model with stable order
        const tempIds: { model: string; id: number; serviceModel: string; multiOrder: number; modelAlias: string }[] = [];
        // Normalize selected list to the enforced order and names
        let selectedNormalized = uiOrder.filter(name => selectedMultiAIModels.some(sel => sel.toLowerCase() === name));
        if (forcedService) {
          const filtered = uiOrder.filter(name => mapUIToService(name) === forcedService);
          if (filtered.length > 0) selectedNormalized = filtered;
        }
        selectedNormalized.forEach((displayName, idx) => {
          const m = displayName;
          const serviceModel = mapUIToService(m);
          const tempId = generateUniqueMessageId();
          const multiOrder = idx + 1; // reserve 0 for user if needed
          tempIds.push({ model: m, id: tempId, serviceModel, multiOrder, modelAlias: m });
          const tempMsg: Message = {
            id: tempId,
            content: "",
            type: "ai",
            timestamp: new Date(),
            model: m,
            displayModel: m,
            isStreaming: true,
            multiBatchId: batchId,
            multiOrder,
            modelAlias: m,
          };
          setMessages((prev) => [...prev, tempMsg]);
        });

        // System prompt is handled server-side by edge functions
        const baseMessages = [
          { role: "user", content: inputValue },
        ];

        const results = await Promise.allSettled(
          tempIds.map(async ({ id, model, serviceModel, multiOrder, modelAlias }) => {
            let streamed = false;
            let full = "";
            try {
              const result = await aiChatService(
                serviceModel,
                baseMessages,
                0.7,
                (chunk: string) => {
                  streamed = true;
                  // Support both cumulative and delta chunk strategies
                  if (chunk && full && chunk.startsWith(full)) {
                    full = chunk;
                  } else {
                    full += chunk;
                  }
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === id
                        ? { ...msg, content: full, isStreaming: true }
                        : msg
                    )
                  );
                },
                (reasoning: string) => {
                  // Live update reasoning while streaming for Gemini
                  if (serviceModel === 'gemini') {
                    setMessages((prev) =>
                      prev.map((msg) => (msg.id === id ? { ...msg, reasoning } : msg))
                    );
                  }
                },
                // userFactsSummaryRef.current // Removed: now handled server-side
              );
              // If provider didn't stream, finalize with returned content
              if (!streamed) {
                const content = (() => {
                  if (typeof result === 'string') return result;
                  if (result && typeof result === 'object') {
                    const r: any = result as any;
                    const c = r.response || r.content || r.message || r?.choices?.[0]?.message?.content || '';
                    return typeof c === 'string' ? c : '';
                  }
                  return '';
                })();
                full = content || full;
              }
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === id
                    ? {
                      ...msg,
                      content: full,
                      isStreaming: false,
                      ...(serviceModel === 'gemini' && lastGeminiReasoning
                        ? { reasoning: lastGeminiReasoning }
                        : {}),
                    }
                    : msg
                )
              );

              // Return the message object to be saved later
              const finalMsg: Message = {
                id,
                content: full,
                type: "ai",
                timestamp: new Date(),
                model: model,
                displayModel: model,
                multiBatchId: batchId,
                multiOrder,
                modelAlias,
                ...(serviceModel === 'gemini' && lastGeminiReasoning ? { reasoning: lastGeminiReasoning } : {}),
              };
              return finalMsg;
            } catch (e: any) {
              const errText = e?.message || "Failed to get response";
              setMessages((prev) => prev.map((msg) => msg.id === id ? { ...msg, content: `Error: ${errText}`, isStreaming: false } : msg));
              // Return error message to be saved
              const errorMsg: Message = {
                id,
                content: `Error: ${errText}`,
                type: "ai",
                timestamp: new Date(),
                model: model,
                displayModel: model,
                multiBatchId: batchId,
                multiOrder,
                modelAlias,
              };
              return errorMsg;
            }
          })
        );

        // After all AI responses are generated, save them sequentially
        if (saveMessageToSupabase) {
          // 1. Save User Message first (it will be buffered by Index.tsx)
          await saveMessageToSupabase(userMessage);

          // 2. Collect and sort AI messages
          const aiMessagesToSave = results
            .filter((r): r is PromiseFulfilledResult<Message> => r.status === 'fulfilled')
            .map(r => r.value)
            .sort((a, b) => (a.multiOrder || 0) - (b.multiOrder || 0));

          // 3. Save AI messages sequentially
          for (const aiMsg of aiMessagesToSave) {
            await saveMessageToSupabase(aiMsg);
          }
        }

      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Check for predefined responses
    let modelToUse = isWebSearchMode ? "web-search" : selectedModel;
    // Map 'glm' to 'ChatGPT' for display
    if (modelToUse === "glm") modelToUse = "ChatGPT";
    const {
      response: predefinedResponse,
      isPredefined,
      searchResults,
    } = await getAIResponse(inputValue, modelToUse);

    // If we have a predefined response, handle it separately
    if (isPredefined) {
      // Add user message
      const userMessage: Message = {
        id: generateUniqueMessageId(),
        content: inputValue,
        type: "user",
        timestamp: new Date(),
        model: isWebSearchMode ? "web-search" : selectedModel,
        selectedAgent: isWebSearchMode ? "web-search" : selectedAgent?.id,
      };

      setMessages((prevMessages) => [...prevMessages, userMessage]);
      // Save user message immediately to Supabase
      if (saveMessageToSupabase) {
        saveMessageToSupabase(userMessage);
      }
      setInputValue("");
      setAttachedMedia(null);

      // Add a small delay to simulate AI thinking
      await new Promise((resolve) => setTimeout(resolve, 1200));

      // Add AI response
      const aiMessage: Message = {
        id: generateUniqueMessageId(),
        content: predefinedResponse,
        type: "ai",
        timestamp: new Date(),
        model: isWebSearchMode ? "web-search" : selectedModel,
        selectedAgent: isWebSearchMode ? "web-search" : selectedAgent?.id,
        searchResults: searchResults,
      };

      setMessages((prevMessages) => [...prevMessages, aiMessage]);
      // Save AI message to Supabase
      if (saveMessageToSupabase) {
        saveMessageToSupabase(aiMessage);
      }
      setIsLoading(false);
      return;
    }

    // Handle image generation mode
    if (isImageMode) {
      if (!inputValue.trim()) {
        setIsLoading(false);
        toast({
          title: "Error",
          description: "Please enter a prompt for image generation",
          variant: "destructive",
        });
        return;
      }

      try {
        // Get user ID for Cloudinary upload
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        let selectedAspectRatio = '1:1';
        try {
          selectedAspectRatio = localStorage.getItem('chatImageAspectRatio') || '1:1';
        } catch {
          // ignore
        }

        const imageList = (attachments || []).filter((a) => a?.type === 'image' && (a.file || a.url)).slice(0, 7);
        const hasImageAttachment = imageList.length > 0;

        // Show the user's prompt as a normal text message (and persist the attached image URL if present)
        let uploadedAttachmentUrls: string[] = [];
        if (hasImageAttachment) {
          // Only upload if there is any File present; if all are URL-only, reuse them directly.
          const needsUpload = !isTempMode && imageList.some((m) => !!m.file);
          if (!needsUpload) {
            uploadedAttachmentUrls = imageList
              .map((m) => m.url)
              .filter((u): u is string => typeof u === 'string' && u.length > 0);
          } else {
            try {
              if (!isTempMode) {
                try { window.dispatchEvent(new Event('attachmentUpload:start')); } catch { }
              }

              const results = await Promise.all(
                imageList.map(async (m) => {
                  if (m.file) {
                    if (isTempMode) return m.url || '';
                    return await uploadAttachmentToCloudinary(m.file);
                  }
                  return m.url || '';
                })
              );

              uploadedAttachmentUrls = results.filter((u) => typeof u === 'string' && u.length > 0);
            } finally {
              if (!isTempMode) {
                try { window.dispatchEvent(new CustomEvent('attachmentUpload:done', { detail: { success: true } })); } catch { }
              }
            }
          }
        }

        const userMessage: Message = {
          id: generateUniqueMessageId(),
          type: "user",
          content: inputValue,
          timestamp: new Date(),
          model: selectedModel,
          attachedMedia: hasImageAttachment
            ? ({ ...(imageList[0] as any), url: uploadedAttachmentUrls[0] || (imageList[0] as any)?.url } as any)
            : undefined,
          attachedMediaList: hasImageAttachment
            ? imageList.map((m, idx) => ({ ...(m as any), url: uploadedAttachmentUrls[idx] || (m as any)?.url } as any))
            : undefined,
          agentData: hasImageAttachment
            ? ({
              ...(undefined as any),
              attachedImageUrls: uploadedAttachmentUrls.slice(0, 7),
            } as any)
            : undefined,
        };

        setMessages((prev: Message[]) => [...prev, userMessage]);
        if (saveMessageToSupabase) {
          // In create-image mode, the assistant response is saved server-side by the edge function.
          // Force-save the user message immediately to avoid leaving it buffered forever.
          saveMessageToSupabase(
            isTempMode
              ? { ...userMessage, attachedMedia: undefined, attachedMediaList: undefined, agentData: undefined, forceSingleSave: true }
              : { ...userMessage, forceSingleSave: true }
          );
        }
        setInputValue("");
        setAttachedMedia(null);

        const response = hasImageAttachment
          ? await supabase.functions.invoke(
            "imageEdit",
            {
              body: {
                prompt: inputValue,
                images: uploadedAttachmentUrls.slice(0, 7),
                aspectRatio: selectedAspectRatio,
                style: 'Image Edit',
                userId: currentUser?.id || null,
                target: 'chat',
                sessionId: currentChatId || null,
              },
            }
          )
          : await supabase.functions.invoke(
            "universal-image-generation",
            {
              body: {
                prompt: inputValue,
                userId: currentUser?.id || null,
                aspectRatio: selectedAspectRatio,
                origin: 'chat'
              },
            }
          );

        if (response.error) throw new Error(response.error.message);
        if (!response.data?.success) throw new Error(response.data?.error || "Image generation failed");

        // The response now contains the direct inference.sh URL
        const aiMessage: Message = {
          id: generateUniqueMessageId(),
          type: "ai",
          content: response.data.image,
          timestamp: new Date(),
          model: "chitra-ai",
          isImage: true,
        };

        setMessages((prev: Message[]) => [...prev, aiMessage]);
        // Save AI message to Supabase
        if (saveMessageToSupabase && !response.data?.chatMessageId) {
          saveMessageToSupabase(aiMessage);
        }
      } catch (error: any) {
        console.error("Image generation failed:", error);
        toast({
          title: "Error",
          description:
            error.message || "Image generation failed. Please try again.",
          variant: "destructive",
        });

        const aiMessage: Message = {
          id: generateUniqueMessageId(),
          type: "ai",
          content:
            "Sorry, I couldn't generate the image. Please try again with a different prompt.",
          timestamp: new Date(),
          model: "chitra-ai",
        };
        setMessages((prev: Message[]) => [...prev, aiMessage]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // Handle media analysis - images and documents
    if (attachedMedia && (attachedMedia.file || attachedMedia.url)) {
      const imageList = (attachments || []).filter(a => a?.type === 'image' && (a.file || a.url)).slice(0, 7);
      const docList = (attachments || []).filter(a => a?.type === 'document' && a.file && !a.file.type.startsWith('image/'));

      // Guard against 0-byte files which will fail FileReader / conversion / uploads
      const hasZeroByte = [...imageList, ...docList].some((m: any) => {
        try {
          const f = m?.file as File | undefined;
          return Boolean(f) && (typeof f.size !== 'number' || f.size <= 0);
        } catch {
          return true;
        }
      });
      if (hasZeroByte) {
        toast({
          title: 'Invalid attachment',
          description: "One or more selected files appear to be 0 bytes (0B) and can't be uploaded.",
          variant: 'destructive',
        });
        return;
      }

      // Mixed attachments: convert docs to images first, then send all images together
      if (docList.length > 0 && (imageList.length > 0 || docList.length > 0)) {
        if (!isTempMode) {
          try { window.dispatchEvent(new Event('attachmentUpload:start')); } catch { }
        }

        // Add user message with the first attachment for backward-compat UI
        const firstDoc = docList[0];
        const originalExt = (() => {
          try {
            const n = (firstDoc.file?.name || '').toLowerCase();
            const ext = n.includes('.') ? n.split('.').pop() : '';
            return ext || undefined;
          } catch { return undefined; }
        })();

        const userMessage: Message = {
          id: generateUniqueMessageId(),
          type: 'user',
          content: inputValue,
          timestamp: new Date(),
          model: 'document-parser',
          attachedMedia: firstDoc,
        };

        setMessages((prev: Message[]) => [...prev, userMessage]);
        setInputValue('');
        setAttachedMedia(null);

        try {
          setIsLoading(true);

          // Convert doc(s) to page images (prefer pre-converted data URLs)
          const toFile = async (dataUrl: string, name: string): Promise<File> => {
            const res = await fetch(dataUrl);
            const blob = await res.blob();
            return new File([blob], name, { type: 'image/png' });
          };

          const docImageUrls: string[] = [];
          for (const doc of docList) {
            const pre = (doc as any)?.convertedImageUrls as string[] | undefined;
            const imgs = (pre && pre.length > 0)
              ? pre.slice(0, 8).map((dataUrl) => ({ dataUrl }))
              : (await convertFileToImages(doc.file)).slice(0, 8);
            const uploads = imgs.map((img: any, idx: number) => {
              const baseName = doc.file.name.replace(/\.[^.]+$/, '');
              return toFile(img.dataUrl, `${baseName}_page_${idx + 1}.png`).then(uploadAttachmentToCloudinary);
            });
            const urls = await Promise.all(uploads);
            docImageUrls.push(...urls);
          }

          // Upload any image attachments (skip Cloudinary for remote URLs)
          const uploadedUserImages: string[] = isTempMode
            ? imageList.map((img) => img.url).filter(Boolean) as string[]
            : (await Promise.all(
              imageList.map(async (img) => {
                if (img.file) return await uploadAttachmentToCloudinary(img.file);
                return img.url || '';
              })
            )).filter((u) => typeof u === 'string' && u.length > 0);

          const allImageUrls = [...docImageUrls, ...uploadedUserImages];
          if (allImageUrls.length === 0) throw new Error('No images could be generated from attachments.');

          // Persist:
          // - doc page images ONLY in metadata (agentData)
          // - real attached image (if present) in attached_media_url via attachedMedia
          if (saveMessageToSupabase) {
            const firstImageUrl = uploadedUserImages[0];
            const persistentUserMessage: Message = {
              ...userMessage,
              attachedMedia: {
                ...userMessage.attachedMedia,
                // If user attached an image along with docs, persist image to attached_media_url.
                // Otherwise, keep this as a doc-only message.
                type: firstImageUrl ? 'image' : 'document',
                url: firstImageUrl || userMessage.attachedMedia.url,
                name: firstImageUrl ? (imageList[0]?.name || imageList[0]?.file?.name || 'image') : (userMessage.attachedMedia?.name || firstDoc.file.name),
              },
              agentData: {
                ...(userMessage as any).agentData,
                attachedMedia: {
                  type: 'document',
                  name: (firstDoc as any)?.name || userMessage.attachedMedia?.name || firstDoc.file.name,
                  pageCount: docImageUrls.length,
                  convertedImageUrls: docImageUrls,
                  originalExt,
                },
                attachedImageUrls: uploadedUserImages,
              },
            };
            saveMessageToSupabase(persistentUserMessage);
          }

          const novaMessages = [
            { role: 'user', content: inputValue }
          ];
          setIsLoading(true);
          const novaResult = await analyzeWithNova({
            messages: novaMessages as any,
            imageUrls: allImageUrls,
            files: []
          });

          const assistantContent =
            novaResult?.choices?.[0]?.message?.content ||
            novaResult?.message?.content ||
            novaResult?.response ||
            'I received the attachments but did not get a textual response.';

          const aiMessage: Message = {
            id: generateUniqueMessageId(),
            type: 'ai',
            content: assistantContent,
            timestamp: new Date(),
            model: selectedModel,
            displayModel: selectedModel,
          };

          setMessages((prev: Message[]) => [...prev, aiMessage]);
          if (saveMessageToSupabase) {
            saveMessageToSupabase(aiMessage);
          }
        } catch (e: any) {
          console.error('Attachment analysis error:', e);
          toast({ title: 'Attachment error', description: e?.message || 'Could not process attachments.', variant: 'destructive' });
        } finally {
          try { window.dispatchEvent(new CustomEvent('attachmentUpload:done', { detail: { success: true } })); } catch { }
          // loader is controlled by request lifecycle; don't force hide here
          setIsLoading(false);
        }
        return;
      }

      if (imageList.length > 1) {
        // Only upload if at least one image has a File; if all are URL-only, skip upload events.
        const needsUpload = !isTempMode && imageList.some((m) => !!m.file);
        if (needsUpload) {
          if (!isTempMode) {
            try { window.dispatchEvent(new Event('attachmentUpload:start')); } catch { }
          }
        }
        try {
          const uploadedUrls: string[] = (await Promise.all(
            imageList.map(async (m) => {
              if (m.file && needsUpload) {
                if (isTempMode) return m.url || '';
                return await uploadAttachmentToCloudinary(m.file);
              }
              return m.url || '';
            })
          )).filter((u) => typeof u === 'string' && u.length > 0);

          if (!uploadedUrls.length) {
            throw new Error('No valid images could be uploaded from the selected attachments.');
          }

          const geminiUrls = uploadedUrls.slice(0, 7);
          let fallbackUrls = geminiUrls.slice(0, 7);
          let extraNote = '';

          if (fallbackUrls.length > 5) {
            const first = fallbackUrls.slice(0, 4);
            const rest = imageList.slice(4, 7);
            const restFiles = rest.map((m) => m.file).filter(Boolean) as File[];
            const restUrls = rest.map((m) => m.url).filter(Boolean) as string[];

            const urlToFile = async (url: string, name: string): Promise<File> => {
              const res = await fetch(url);
              const blob = await res.blob();
              const type = blob.type || 'image/png';
              return new File([blob], name, { type });
            };

            let collageUrl: string | null = null;
            if (precomputedFallbackCollage && (precomputedFallbackCollage.file || precomputedFallbackCollage.dataUrl)) {
              if (!isTempMode && precomputedFallbackCollage.file) {
                collageUrl = await uploadAttachmentToCloudinary(precomputedFallbackCollage.file);
              } else if (precomputedFallbackCollage.dataUrl) {
                collageUrl = precomputedFallbackCollage.dataUrl;
              } else if (precomputedFallbackCollage.file) {
                const collageDataUrl = await new Promise<string>((resolve) => {
                  const r = new FileReader();
                  r.onload = () => resolve(r.result as string);
                  r.readAsDataURL(precomputedFallbackCollage.file as File);
                });
                collageUrl = collageDataUrl;
              }
            }

            if (!collageUrl && restFiles.length > 0) {
              const label = "It's a collage of 3 diffrent seprate image";
              const collage = await createLabeledCollageFromImages(restFiles.slice(0, 3), label);
              if (!isTempMode) {
                collageUrl = await uploadAttachmentToCloudinary(collage.file);
              } else {
                collageUrl = collage.dataUrl;
              }
            } else if (!collageUrl && restUrls.length > 0) {
              // Even when images come from remote URLs (library/history), build a real collage for fallback.
              const files = await Promise.all(
                restUrls.slice(0, 3).map((u, i) => urlToFile(u, `fallback_${i + 1}.png`))
              );
              const collage = await createLabeledCollageFromImages(files, "It's a collage of 3 diffrent seprate image");
              if (!isTempMode) {
                collageUrl = await uploadAttachmentToCloudinary(collage.file);
              } else {
                collageUrl = collage.dataUrl;
              }
            }

            if (collageUrl) {
              fallbackUrls = [...first, collageUrl].slice(0, 5);
              extraNote = "\n\nNote: the last image is a collage of multiple separate images.";
            } else {
              fallbackUrls = first.slice(0, 4);
            }
          }

          const uploadedMediaList: AttachedMedia[] = imageList.map((m, idx) => ({ ...(m as any), url: uploadedUrls[idx] || (m as any)?.url } as any));
          const userMessage: Message = {
            id: generateUniqueMessageId(),
            type: 'user',
            content: inputValue || 'Attached images',
            timestamp: new Date(),
            model: selectedModel,
            displayModel: selectedModel,
            attachedMedia: uploadedMediaList[0],
            attachedMediaList: uploadedMediaList,
            agentData: {
              attachedImageUrls: uploadedUrls.slice(0, 7),
            } as any,
          };

          setMessages((prev: Message[]) => [...prev, userMessage]);
          if (saveMessageToSupabase) {
            saveMessageToSupabase(isTempMode ? { ...userMessage, attachedMedia: undefined, attachedMediaList: undefined } : userMessage);
          }

          setInputValue('');
          setAttachedMedia(null);

          const novaMessages = [
            { role: 'user', content: String((inputValue || 'Please analyze these images and describe what you see in detail') + extraNote) }
          ];
          setIsLoading(true);
          const novaResult = await analyzeWithNova({
            messages: novaMessages as any,
            imageUrls: geminiUrls,
            imageUrlsFallback: fallbackUrls,
            files: []
          });

          const assistantContent =
            novaResult?.choices?.[0]?.message?.content ||
            novaResult?.message?.content ||
            novaResult?.response ||
            'I received the images but did not get a textual response.';

          const aiMessage: Message = {
            id: generateUniqueMessageId(),
            type: 'ai',
            content: assistantContent,
            timestamp: new Date(),
            model: selectedModel,
            displayModel: selectedModel,
          };

          setMessages((prev: Message[]) => [...prev, aiMessage]);
          if (saveMessageToSupabase) {
            saveMessageToSupabase(aiMessage);
          }
        } catch (e: any) {
          toast({ title: 'Image upload failed', description: e?.message || 'Could not upload images. Please try again.', variant: 'destructive' });
        } finally {
          if (needsUpload && !isTempMode) {
            try { window.dispatchEvent(new CustomEvent('attachmentUpload:done', { detail: { success: true } })); } catch { }
          }
          // loader is controlled by request lifecycle; don't force hide here
          setIsLoading(false);
        }

        return;
      }

      // Handle document parsing (non-image files)
      if (
        attachedMedia.type === "document" &&
        !attachedMedia.file.type.startsWith("image/")
      ) {
        const originalExt = (() => {
          try {
            const n = (attachedMedia.file?.name || '').toLowerCase();
            const ext = n.includes('.') ? n.split('.').pop() : '';
            return ext || undefined;
          } catch { return undefined; }
        })();
        const userMessage: Message = {
          id: generateUniqueMessageId(),
          type: "user",
          content: inputValue,
          timestamp: new Date(),
          model: "document-parser",
          attachedMedia,
        };

        setMessages((prev: Message[]) => [...prev, userMessage]);
        setInputValue("");
        setAttachedMedia(null); // Remove attachment immediately

        try {
          setIsLoading(true);
          // Convert file to base64 (raw) for Nova attachments
          try { window.dispatchEvent(new Event('attachmentUpload:start')); } catch { }
          const pre = (attachedMedia as any)?.convertedImageUrls as string[] | undefined;
          const pageImages = (pre && pre.length > 0)
            ? pre.slice(0, 8).map((dataUrl) => ({ dataUrl }))
            : (await convertFileToImages(attachedMedia.file)).slice(0, 8);
          if (pageImages.length === 0) {
            throw new Error('No images could be generated from the file.');
          }
          const toFile = async (dataUrl: string, name: string): Promise<File> => {
            const res = await fetch(dataUrl);
            const blob = await res.blob();
            return new File([blob], name, { type: 'image/png' });
          };
          const uploadPromises = (pageImages as any[]).map((img, idx) => toFile(img.dataUrl, `${attachedMedia.file.name.replace(/\.[^.]+$/, '')}_page_${idx + 1}.png`).then(uploadAttachmentToCloudinary));
          const imageUrls = await Promise.all(uploadPromises);

          // Persist the Cloudinary URLs that were sent to Nova (avoid storing blob: urls)
          if (saveMessageToSupabase) {
            const persistentUserMessage: Message = {
              ...userMessage,
              attachedMedia: {
                ...userMessage.attachedMedia,
                url: imageUrls[0] || userMessage.attachedMedia.url,
                pageCount: imageUrls.length,
                convertedImageUrls: imageUrls,
                originalExt,
              },
              agentData: {
                ...(userMessage as any).agentData,
                attachedMedia: {
                  type: 'document',
                  name: userMessage.attachedMedia?.name || attachedMedia.file.name,
                  pageCount: imageUrls.length,
                  convertedImageUrls: imageUrls,
                  originalExt,
                },
              },
            };
            saveMessageToSupabase(persistentUserMessage);
          }

          // Send file and query to OpenRouter Nova via edge function
          const novaMessages = [
            { role: 'user', content: inputValue }
          ];
          const novaResult = await analyzeWithNova({
            messages: novaMessages as any,
            imageUrls: imageUrls,
            files: []
          });

          const assistantContent =
            novaResult?.choices?.[0]?.message?.content ||
            novaResult?.message?.content ||
            novaResult?.response ||
            'I received the document but did not get a textual response.';

          const aiMessage: Message = {
            id: generateUniqueMessageId(),
            type: "ai",
            content: assistantContent,
            timestamp: new Date(),
            model: selectedModel,
            displayModel: selectedModel,
          };

          setMessages((prev: Message[]) => [...prev, aiMessage]);

          if (saveMessageToSupabase) {
            saveMessageToSupabase(aiMessage);
          }
        } catch (error) {
          console.error("Document parsing error:", error);
          const errorMessage: Message = {
            id: generateUniqueMessageId(),
            type: "ai",
            content: `Sorry, I couldn't analyze the document "${attachedMedia.file.name
              }". \n\nPlease try again with a different document or check if the file format is supported.`,
            timestamp: new Date(),
            model: selectedModel,
          };

          setMessages((prev: Message[]) => [...prev, errorMessage]);

          if (saveMessageToSupabase) {
            saveMessageToSupabase(errorMessage);
          }
        } finally {
          try { window.dispatchEvent(new CustomEvent('attachmentUpload:done', { detail: { success: true } })); } catch { }
          setIsLoading(false);
        }
        return;
      }

      if (attachedMedia.type === "image") {
        // If there is no File, assume the image was already uploaded (or is a remote URL) and do not re-upload.
        let uploadedMedia: AttachedMedia;
        if (!attachedMedia.file || isTempMode) {
          uploadedMedia = { ...attachedMedia } as AttachedMedia;
        } else {
          if (!isTempMode) {
            try { window.dispatchEvent(new Event('attachmentUpload:start')); } catch { }
          }
          let uploadedUrl: string | null = null;
          try {
            if (!isTempMode) {
              uploadedUrl = await uploadAttachmentToCloudinary(attachedMedia.file);
            }
          } catch (e: any) {
            toast({
              title: "Image upload failed",
              description: e?.message || "Could not upload image. Please try again.",
              variant: "destructive",
            });
            if (!isTempMode) {
              try { window.dispatchEvent(new CustomEvent('attachmentUpload:done', { detail: { success: false } })); } catch { }
            }
            return;
          } finally {
            if (!isTempMode) {
              try { window.dispatchEvent(new CustomEvent('attachmentUpload:done', { detail: { success: true } })); } catch { }
            }
          }
          uploadedMedia = isTempMode
            ? ({ ...attachedMedia } as AttachedMedia)
            : ({ ...attachedMedia, url: uploadedUrl! } as AttachedMedia);
        }
        const userMessage: Message = {
          id: generateUniqueMessageId(),
          type: "user",
          content: inputValue || "Analyze this image",
          timestamp: new Date(),
          model: selectedModel,
          displayModel: selectedModel,
          attachedMedia: uploadedMedia,
        };

        setMessages((prev: Message[]) => [...prev, userMessage]);
        // Save user message immediately to Supabase (omit attachment in temp mode)
        if (saveMessageToSupabase) {
          saveMessageToSupabase(isTempMode ? { ...userMessage, attachedMedia: undefined } : userMessage);
        }
        setInputValue("");
        setAttachedMedia(null);
        setIsLoading(true);
        // For already-uploaded images we never dispatched start, so no need to dispatch done here.

        try {
          const base64Data = attachedMedia.file
            ? await new Promise<string>((resolve, reject) => {
              const fileReader = new FileReader();
              fileReader.onload = () => resolve(fileReader.result as string);
              fileReader.onerror = reject;
              fileReader.readAsDataURL(attachedMedia.file as File);
            })
            : await urlToDataUrl(attachedMedia.url);

          const response = await visionChatService(
            inputValue ||
            "Please analyze this image and describe what you see in detail",
            base64Data
          );

          const aiMessage: Message = {
            id: generateUniqueMessageId(),
            type: "ai",
            content: response.content,
            timestamp: new Date(),
            model: selectedModel,
            displayModel: selectedModel,
          };

          setMessages((prev: Message[]) => [...prev, aiMessage]);
          // Save AI message to Supabase
          if (saveMessageToSupabase) {
            saveMessageToSupabase(aiMessage);
          }
        } catch (error: any) {
          console.error("Image analysis failed:", error);
          toast({
            title: "❌ Image Analysis Error",
            description:
              error.message || "Failed to analyze the image. Please try again.",
            variant: "destructive",
          });

          const errorMessage: Message = {
            id: generateUniqueMessageId(),
            type: "ai",
            content: "Sorry, I couldn't analyze the image. Please try again.",
            timestamp: new Date(),
            model: selectedModel,
            displayModel: selectedModel,
          };
          setMessages((prev: Message[]) => [...prev, errorMessage]);
        } finally {
          setIsLoading(false);
        }
        return;
      }
    }

    let finalAttachedMedia = attachedMedia as AttachedMedia | null;
    if (finalAttachedMedia && finalAttachedMedia.type === 'image' && finalAttachedMedia.file) {
      if (!isTempMode) {
        try { window.dispatchEvent(new Event('attachmentUpload:start')); } catch { }
      }
      try {
        if (!isTempMode) {
          const cloudUrl = await uploadAttachmentToCloudinary(finalAttachedMedia.file);
          finalAttachedMedia = { ...finalAttachedMedia, url: cloudUrl };
        }
      } catch (e: any) {
        toast({ title: 'Image upload failed', description: e?.message || 'Could not upload image. Please try again.', variant: 'destructive' });
        if (!isTempMode) {
          try { window.dispatchEvent(new CustomEvent('attachmentUpload:done', { detail: { success: false } })); } catch { }
        }
        return;
      } finally {
        if (!isTempMode) {
          try { window.dispatchEvent(new CustomEvent('attachmentUpload:done', { detail: { success: true } })); } catch { }
        }
      }
      setAttachedMedia(null);
      setIsLoading(true);
    }

    const contentToSave = selectedAgent?.id === 'weather'
      ? (inputValue?.split(',')[0]?.trim() || inputValue)
      : inputValue;

    const userMessage: Message = {
      id: generateUniqueMessageId(),
      type: "user",
      content: contentToSave,
      timestamp: new Date(),
      model: selectedAgent ? selectedAgent.id : selectedModel,
      selectedAgent: selectedAgent?.id,
      attachedMedia: finalAttachedMedia || undefined,
    };

    setMessages((prev: Message[]) => [...prev, userMessage]);
    // Save user message immediately to Supabase (omit attachment in temp mode)
    if (saveMessageToSupabase) {
      saveMessageToSupabase(isTempMode ? { ...userMessage, attachedMedia: undefined } : userMessage);
    }
    setInputValue("");

    try {
      let aiResponse = "";
      let reasoningContent: string | undefined = undefined;
      let weather: any = undefined; // <-- Declare weather in parent scope

      // Get relevant chat memory for context
      const memoryContext = userFactsSummaryRef.current ? `User Facts Summary:\n${userFactsSummaryRef.current}\n(Use this information when required and keep it separate from user prompt)` : "";

      // Check for previously parsed document content
      const lastParsedDoc = localStorage.getItem("lastParsedDocument");
      let documentContext = "";
      if (lastParsedDoc) {
        try {
          const parsedDoc = JSON.parse(lastParsedDoc);
          // Only include document context if it's recent (within last hour)
          if (Date.now() - parsedDoc.timestamp < 3600000) {
            documentContext = `\n\nDocument Context (from ${parsedDoc.filename
              }):\n${parsedDoc.content.substring(0, 2000)}${parsedDoc.content.length > 2000 ? "..." : ""
              }`;
          }
        } catch (e) {
          console.error("Error parsing document context:", e);
        }
      }

      // Prepare input with memory and document context
      let inputWithContext = inputValue;
      if (memoryContext) {
        inputWithContext = `${memoryContext}\n\nCurrent Query: ${inputValue}`;
      }
      if (documentContext) {
        inputWithContext += documentContext;
      }

      // Handle GPT-OSS model with optimized streaming
      if (selectedModel === "gpt-oss") {
        const tempAiMessageId = generateUniqueMessageId();
        const tempAiMessage: Message = {
          id: tempAiMessageId,
          type: "ai",
          content: "",
          timestamp: new Date(),
          model: "gpt-oss",
          isStreaming: true, // Add streaming indicator
        };

        setMessages((prev) => [...prev, tempAiMessage]);

        try {
          let buffer = "";
          let lastUpdate = 0;
          let animationFrameId: number | null = null;
          let hasStructuredContent = false;
          let updateCount = 0;

          // Optimized update function with smart throttling for structured content
          const updateMessage = (final = false) => {
            if (animationFrameId && !final) return; // Skip if already scheduled

            animationFrameId = requestAnimationFrame(() => {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempAiMessageId
                    ? {
                      ...msg,
                      content: buffer,
                      isStreaming: !final, // Clear streaming flag on final update
                    }
                    : msg
                )
              );
              animationFrameId = null;
              lastUpdate = performance.now();
              updateCount++;
            });
          };

          // Call the streaming version of the service
          await aiChatService(
            "gpt-oss",
            [{ role: "user", content: inputValue }], // Use clean input, pass facts separately
            0.7, // temperature
            (chunk) => {
              // Buffer the chunks
              buffer += chunk;

              // Detect structured content early
              if (
                !hasStructuredContent &&
                (chunk.includes("```") || chunk.includes("|"))
              ) {
                hasStructuredContent = true;
              }

              const now = performance.now();
              const timeSinceLastUpdate = now - lastUpdate;

              // Different throttling strategies based on content type
              if (hasStructuredContent) {
                // For structured content, update less frequently to avoid expensive re-renders
                if (timeSinceLastUpdate > 50) {
                  // ~20fps for structured content
                  updateMessage();
                } else if (!animationFrameId && updateCount < 5) {
                  // Allow a few quick updates at the start, then throttle heavily
                  updateMessage();
                }
              } else {
                // For regular text, use high frequency updates
                if (timeSinceLastUpdate > 8) {
                  // ~120fps for regular text
                  updateMessage();
                } else if (!animationFrameId) {
                  updateMessage();
                }
              }
            },
            undefined, // onReasoning
            userFactsSummaryRef.current // userFactsSummary
          );

          // Final update to ensure all content is rendered
          if (buffer) {
            updateMessage(true); // Force final update
            // Save final AI message to Supabase (paired with buffered user)
            if (saveMessageToSupabase) {
              const finalAiMessage: Message = {
                id: tempAiMessageId,
                type: "ai",
                content: buffer,
                timestamp: new Date(),
                model: "gpt-oss",
              };
              saveMessageToSupabase(finalAiMessage);
            }
          }

          setIsLoading(false);
          return;
        } catch (error: any) {
          console.error("Error in Chat GPT streaming:", error);
          // Update the message with the error
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempAiMessageId
                ? {
                  ...msg,
                  content:
                    "❌ Error getting response from Chat GPT. Please try again.",
                }
                : msg
            )
          );
          setIsLoading(false);
          return;
        }
      }

      // Handle Qwen AI model
      if (selectedModel === "qwen") {
        try {
          const qwenResponse = await callQwenAI(inputWithContext);

          const qwenAiMessage: Message = {
            id: generateUniqueMessageId(),
            type: "ai",
            content: qwenResponse.response,
            timestamp: new Date(),
            model: "qwen",
            reasoning: qwenResponse.reasoning || undefined,
          };

          setMessages((prev: Message[]) => [...prev, qwenAiMessage]);
          // Save AI message to Supabase
          if (saveMessageToSupabase) {
            saveMessageToSupabase(qwenAiMessage);
          }
          // Memory saving removed
          setIsLoading(false);
          return;
        } catch (error) {
          console.error("Error calling Qwen AI:", error);
          const errorMessage: Message = {
            id: generateUniqueMessageId(),
            type: "ai",
            content:
              "Sorry, there was an error getting a response from Qwen AI.",
            timestamp: new Date(),
            model: "qwen",
            agentData: { error: true },
          };

          setMessages((prev: Message[]) => [...prev, errorMessage]);
          setIsLoading(false);
          return;
        }
      }

      // Handle Sambanova AI model with optimized streaming
      if (selectedModel === "sambanova") {
        const tempAiMessageId = generateUniqueMessageId();
        const tempAiMessage: Message = {
          id: tempAiMessageId,
          type: "ai",
          content: "",
          timestamp: new Date(),
          model: "sambanova",
          isStreaming: true, // Add streaming indicator
        };

        setMessages((prev) => [...prev, tempAiMessage]);

        try {
          let buffer = "";
          let lastUpdate = 0;
          let animationFrameId: number | null = null;
          let hasStructuredContent = false;
          let updateCount = 0;

          // Optimized update function with smart throttling for structured content
          const updateMessage = (final = false) => {
            if (animationFrameId && !final) return; // Skip if already scheduled

            animationFrameId = requestAnimationFrame(() => {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempAiMessageId
                    ? {
                      ...msg,
                      content: buffer,
                      isStreaming: !final, // Clear streaming flag on final update
                    }
                    : msg
                )
              );
              animationFrameId = null;
              lastUpdate = performance.now();
              updateCount++;
            });
          };

          // Call the streaming version of the service
          // System prompt is handled server-side by edge functions
          await aiChatService(
            "sambanova",
            [
              { role: "user", content: inputWithContext },
            ],
            0.7, // temperature
            (chunk) => {
              // Buffer the chunks
              buffer += chunk;

              // Detect structured content early
              if (
                !hasStructuredContent &&
                (chunk.includes("```") || chunk.includes("|"))
              ) {
                hasStructuredContent = true;
              }

              const now = performance.now();
              const timeSinceLastUpdate = now - lastUpdate;

              // Different throttling strategies based on content type
              if (hasStructuredContent) {
                // For structured content, update less frequently to avoid expensive re-renders
                if (timeSinceLastUpdate > 50) {
                  // ~20fps for structured content
                  updateMessage();
                } else if (!animationFrameId && updateCount < 5) {
                  // Allow a few quick updates at the start, then throttle heavily
                  updateMessage();
                }
              } else {
                // For regular text, use high frequency updates
                if (timeSinceLastUpdate > 8) {
                  // ~120fps for regular text
                  updateMessage();
                } else if (!animationFrameId) {
                  updateMessage();
                }
              }
            }
          );

          // Final update to ensure all content is rendered
          if (buffer) {
            updateMessage(true); // Force final update
            // Save final AI message to Supabase (paired with buffered user)
            if (saveMessageToSupabase) {
              const finalAiMessage: Message = {
                id: tempAiMessageId,
                type: "ai",
                content: buffer,
                timestamp: new Date(),
                model: "sambanova",
              };
              saveMessageToSupabase(finalAiMessage);
            }
          }

          setIsLoading(false);
          return;
        } catch (error: any) {
          console.error("Error in Sambanova streaming:", error);
          // Update the message with the error
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempAiMessageId
                ? {
                  ...msg,
                  content:
                    "❌ Error getting response from Sambanova AI. Please try again.",
                }
                : msg
            )
          );
          setIsLoading(false);
          return;
        }
      }

      // Handle Kimi AI model with streaming via Edge Function
      if (selectedModel === "kimi") {
        const tempAiMessageId = generateUniqueMessageId();
        const tempAiMessage: Message = {
          id: tempAiMessageId,
          type: "ai",
          content: "",
          timestamp: new Date(),
          model: "kimi",
          isStreaming: true,
        };

        setMessages((prev) => [...prev, tempAiMessage]);

        try {
          let buffer = "";
          // System prompt is handled server-side by edge functions
          await aiChatService(
            "kimi",
            [
              { role: "user", content: inputWithContext },
            ],
            0.6,
            (chunk: string) => {
              buffer += chunk;
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempAiMessageId
                    ? { ...msg, content: buffer, isStreaming: true }
                    : msg
                )
              );
            }
          );

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempAiMessageId ? { ...msg, isStreaming: false } : msg
            )
          );

          if (saveMessageToSupabase) {
            const finalAiMessage: Message = {
              id: tempAiMessageId,
              type: "ai",
              content: buffer,
              timestamp: new Date(),
              model: "kimi",
              isStreaming: false,
            };
            saveMessageToSupabase(finalAiMessage);
          }

          setIsLoading(false);
          return;
        } catch (error: any) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempAiMessageId
                ? {
                  ...msg,
                  content: `❌ Error getting response from Kimi: ${error.message}`,
                  isStreaming: false,
                }
                : msg
            )
          );
          setIsLoading(false);
          return;
        }
      }

      // Handle Perplexity AI model (non-streaming from Edge Function)
      if (selectedModel === "perplexity") {
        const tempAiMessageId = generateUniqueMessageId();
        const tempAiMessage: Message = {
          id: tempAiMessageId,
          type: "ai",
          content: "",
          timestamp: new Date(),
          model: "perplexity",
          isStreaming: true,
        } as any;

        setMessages((prev) => [...prev, tempAiMessage]);

        try {
          const result: any = await aiChatService(
            "perplexity",
            [{ role: "user", content: inputWithContext }],
            0.7
          );

          const content = ((): string => {
            if (!result) return "";
            if (typeof result === "string") return result;
            return (
              result.content ||
              result.response ||
              result.message ||
              result?.choices?.[0]?.message?.content ||
              ""
            );
          })();

          const citations = (result as any)?.citations || [];
          const search_results = (result as any)?.search_results || [];
          const links = Array.isArray(search_results)
            ? search_results
              .filter((r: any) => r?.url)
              .map((r: any) => ({
                title: r.title || r.url,
                url: r.url,
                snippet: r.snippet,
              }))
            : [];

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempAiMessageId
                ? {
                  ...msg,
                  content,
                  isStreaming: false,
                  ...(links.length
                    ? {
                      searchResults: {
                        query: inputValue,
                        answer: content,
                        results: links.map((l) => ({ title: l.title, url: l.url, content: l.snippet || '' })),
                        citations,
                        perplexity: true,
                      },
                    }
                    : {}),
                }
                : msg
            )
          );

          if (saveMessageToSupabase) {
            const finalAiMessage: Message = {
              id: tempAiMessageId,
              type: 'ai',
              content,
              timestamp: new Date(),
              model: 'perplexity',
              ...(links.length
                ? {
                  searchResults: {
                    query: inputValue,
                    answer: content,
                    results: links.map((l) => ({ title: l.title, url: l.url, content: l.snippet || '' })),
                    citations,
                    perplexity: true,
                  },
                }
                : {}),
            } as any;
            saveMessageToSupabase(finalAiMessage);
          }

          setIsLoading(false);
          return;
        } catch (error: any) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempAiMessageId
                ? {
                  ...msg,
                  content: `❌ Error getting response from Perplexity: ${error.message}`,
                  isStreaming: false,
                }
                : msg
            )
          );
          setIsLoading(false);
          return;
        }
      }

      // Handle Cerebras AI model with optimized streaming
      if (selectedModel === "cerebras") {
        const tempAiMessageId = generateUniqueMessageId();
        const tempAiMessage: Message = {
          id: tempAiMessageId,
          type: "ai",
          content: "",
          timestamp: new Date(),
          model: "cerebras",
          isStreaming: true, // Add streaming indicator
        };

        setMessages((prev) => [...prev, tempAiMessage]);

        try {
          let buffer = "";
          let lastUpdate = 0;
          let animationFrameId: number | null = null;
          let hasStructuredContent = false;
          let updateCount = 0;

          // Optimized update function with smart throttling for structured content
          const updateMessage = (final = false) => {
            if (animationFrameId && !final) return; // Skip if already scheduled

            animationFrameId = requestAnimationFrame(() => {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempAiMessageId
                    ? {
                      ...msg,
                      content: buffer,
                      isStreaming: !final, // Clear streaming flag on final update
                    }
                    : msg
                )
              );
              animationFrameId = null;
              lastUpdate = performance.now();
              updateCount++;
            });
          };

          // Call the streaming version of the service
          // System prompt is handled server-side by edge functions
          await aiChatService(
            "cerebras",
            [
              { role: "user", content: inputWithContext },
            ],
            0.7, // temperature
            (chunk) => {
              // Buffer the chunks
              buffer += chunk;

              // Detect structured content early
              if (
                !hasStructuredContent &&
                (chunk.includes("```") || chunk.includes("|"))
              ) {
                hasStructuredContent = true;
              }

              const now = performance.now();
              const timeSinceLastUpdate = now - lastUpdate;

              // Different throttling strategies based on content type
              if (hasStructuredContent) {
                // For structured content, update less frequently to avoid expensive re-renders
                if (timeSinceLastUpdate > 50) {
                  // ~20fps for structured content
                  updateMessage();
                } else if (!animationFrameId && updateCount < 5) {
                  // Allow a few quick updates at the start, then throttle heavily
                  updateMessage();
                }
              } else {
                // For regular text, use high frequency updates
                if (timeSinceLastUpdate > 8) {
                  // ~120fps for regular text
                  updateMessage();
                } else if (!animationFrameId) {
                  updateMessage();
                }
              }
            }
          );

          // Final update to ensure all content is rendered
          if (buffer) {
            updateMessage(true); // Force final update
            // Save final AI message to Supabase (paired with buffered user)
            if (saveMessageToSupabase) {
              const finalAiMessage: Message = {
                id: tempAiMessageId,
                type: "ai",
                content: buffer,
                timestamp: new Date(),
                model: "cerebras",
              };
              saveMessageToSupabase(finalAiMessage);
            }
          }

          setIsLoading(false);
          return;
        } catch (error: any) {
          // Update the message with the error
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempAiMessageId
                ? {
                  ...msg,
                  content:
                    "❌ Error getting response from Cerebras AI. Please try again.",
                }
                : msg
            )
          );
          setIsLoading(false);
          return;
        }
      }

      // Handle Upstage Solar Pro AI model with optimized streaming
      if (selectedModel === "upstage") {
        const tempAiMessageId = generateUniqueMessageId();
        const tempAiMessage: Message = {
          id: tempAiMessageId,
          type: "ai",
          content: "",
          timestamp: new Date(),
          model: "upstage",
          isStreaming: true, // Add streaming indicator
        };

        setMessages((prev) => [...prev, tempAiMessage]);

        try {
          let buffer = "";
          let lastUpdate = 0;
          let animationFrameId: number | null = null;
          let hasStructuredContent = false;
          let updateCount = 0;

          // Optimized update function with smart throttling for structured content
          const updateMessage = (final = false) => {
            if (animationFrameId && !final) return; // Skip if already scheduled

            animationFrameId = requestAnimationFrame(() => {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempAiMessageId
                    ? {
                      ...msg,
                      content: buffer,
                      isStreaming: !final, // Clear streaming flag on final update
                    }
                    : msg
                )
              );
              animationFrameId = null;
              lastUpdate = performance.now();
              updateCount++;
            });
          };

          // System prompt is handled server-side by edge functions
          await upstageAiService(
            [
              { role: "user", content: inputWithContext },
            ],
            (chunk) => {
              // Buffer the chunks
              buffer += chunk;

              // Detect structured content early
              if (
                !hasStructuredContent &&
                (chunk.includes("```") || chunk.includes("|"))
              ) {
                hasStructuredContent = true;
              }

              const now = performance.now();
              const timeSinceLastUpdate = now - lastUpdate;

              // Different throttling strategies based on content type
              if (hasStructuredContent) {
                // For structured content, update less frequently to avoid expensive re-renders
                if (timeSinceLastUpdate > 50) {
                  // ~20fps for structured content
                  updateMessage();
                } else if (!animationFrameId && updateCount < 5) {
                  // Allow a few quick updates at the start, then throttle heavily
                  updateMessage();
                }
              } else {
                // For regular text, use high frequency updates
                if (timeSinceLastUpdate > 8) {
                  // ~120fps for regular text
                  updateMessage();
                } else if (!animationFrameId) {
                  updateMessage();
                }
              }
            },
            0.7 // temperature
          );

          // Final update to ensure all content is rendered
          if (buffer) {
            updateMessage(true); // Force final update
            // Save final AI message to Supabase (paired with buffered user)
            if (saveMessageToSupabase) {
              const finalAiMessage: Message = {
                id: tempAiMessageId,
                type: "ai",
                content: buffer,
                timestamp: new Date(),
                model: "upstage",
              };
              saveMessageToSupabase(finalAiMessage);
            }
          }

          setIsLoading(false);
          return;
        } catch (error: any) {
          console.error("Error in Upstage streaming:", error);
          // Update the message with the error
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempAiMessageId
                ? {
                  ...msg,
                  content:
                    "❌ Error getting response from Solar Pro AI. Please try again.",
                }
                : msg
            )
          );
          setIsLoading(false);
          return;
        }
      }

      // Handle agent-specific responses
      if (selectedAgent) {
        switch (selectedAgent.id) {
          case "weather":
            try {
              const userText = inputValue.trim() || "New York";
              // Prefer stashed coordinates from LocationButton for backend fetch,
              // but keep user's text for message persistence/display.
              let locationToSend = userText;
              try {
                const raw = localStorage.getItem('weather:lastCoords');
                if (raw) {
                  const parsed = JSON.parse(raw);
                  if (parsed && typeof parsed.lat === 'number' && typeof parsed.lon === 'number') {
                    locationToSend = `${parsed.lat},${parsed.lon}`;
                  }
                }
              } catch { }

              const consolidated = await fetchWeather(locationToSend, {
                autoDetect: false,
              });
              // Clear after use to avoid stale reuse
              try { localStorage.removeItem('weather:lastCoords'); } catch { }
              // Override display location with user's typed label (privacy-safe)
              const cleanLabel = (userText.split(',')[0] || userText).trim();
              const mergedWeather = { ...consolidated, location: cleanLabel };
              weather = mergedWeather;
              aiResponse = "";
            } catch (error: any) {
              aiResponse = `❌ Weather Error: ${error.message}`;
            }
            break;

          case "wikipedia":
            try {
              const wikiData = await wikipediaService(inputValue.trim());
              if (wikiData) {
                // Store Wikipedia data as JSON string for proper rendering
                aiResponse = JSON.stringify({ wikipedia: wikiData });
              } else {
                aiResponse = `No Wikipedia article found for "${inputValue.trim()}". Please try a different search term.`;
              }
            } catch (error: any) {
              aiResponse = `❌ Wikipedia Error: ${error.message}`;
            }
            break;

          case "video-summary":
            try {
              const result = await videoSummaryService(inputValue.trim());
              aiResponse = formatVideoSummaryResponse(result, Boolean(isDarkMode));
            } catch (error: any) {
              aiResponse = `❌ Video Summary Error: ${error?.message || 'Failed to summarize the provided video URL.'}`;
            }
            break;

          case "giphy":
            try {
              const gifData = await giphyService(inputValue.trim());
              aiResponse = formatGiphyResponse(
                gifData,
                inputValue.trim(),
                Boolean(isDarkMode)
              );
            } catch (error: any) {
              aiResponse = `❌ GIF Search Error: ${error.message}`;
            }
            break;

          case "recipe":
          case "recipes":
            try {
              const recipeData = await recipeService(inputValue.trim());
              aiResponse = recipeData; // recipeService now returns formatted string directly
            } catch (error: any) {
              aiResponse = `❌ Recipe Error: ${error.message}`;
            }
            break;

          case "youtube":
            try {
              // Parse search filters from input
              const searchInput = inputValue.trim();
              let searchQuery = searchInput;
              let searchType: "video" | "channel" | "all" = "all";
              let orderBy:
                | "relevance"
                | "date"
                | "rating"
                | "viewCount"
                | "title" = "relevance";

              // Extract filters from input (e.g., "search videos for cats" or "find channels about cooking")
              if (searchInput.toLowerCase().includes("video")) {
                searchType = "video";
                searchQuery = searchInput
                  .replace(/(search|find|videos?|for)/gi, "")
                  .trim();
              } else if (searchInput.toLowerCase().includes("channel")) {
                searchType = "channel";
                searchQuery = searchInput
                  .replace(/(search|find|channels?|about)/gi, "")
                  .trim();
              }

              // Extract ordering preferences
              if (
                searchInput.toLowerCase().includes("recent") ||
                searchInput.toLowerCase().includes("latest")
              ) {
                orderBy = "date";
              } else if (
                searchInput.toLowerCase().includes("popular") ||
                searchInput.toLowerCase().includes("views")
              ) {
                orderBy = "viewCount";
              }

              const youtubeData = await youtubeService(
                searchQuery || searchInput,
                searchType,
                8,
                orderBy
              );
              aiResponse = JSON.stringify({
                __youtube: true,
                data: youtubeData,
                query: searchQuery || searchInput,
                isDarkMode: Boolean(isDarkMode),
              });
            } catch (error: any) {
              aiResponse = `❌ YouTube Search Error: ${error.message}`;
            }
            break;

          case "meme":
          case "memes":
            try {
              // Check if user input is empty or asking for templates
              if (
                !inputValue.trim() ||
                inputValue.toLowerCase().includes("template") ||
                inputValue.toLowerCase().includes("refresh") ||
                inputValue.toLowerCase().includes("show")
              ) {
                // Show meme templates
                const result = await showMemeTemplatesService();
                setMemeTemplates(result.templates);
                setShowMemeTemplates(true);
                aiResponse = JSON.stringify(result); // Will be handled by special rendering
              } else {
                // Generate meme with AI
                const result = await generateMemeWithAI(inputValue.trim());
                aiResponse = JSON.stringify(result); // Will be handled by special rendering
              }
            } catch (error: any) {
              aiResponse = `❌ Meme Error: ${error.message}`;
            }
            break;

          case "calculator":
            try {
              const { result, explanation } = await gptOssCalculatorWithExplanation(inputValue.trim());
              aiResponse = JSON.stringify({
                type: 'calculator',
                data: { expression: inputValue, result, explanation }
              });
            } catch (error: any) {
              aiResponse = JSON.stringify({ type: 'calculator', data: { error: String(error.message || error) } });
            }
            break;

          case "qr-code":
            try {
              const text = inputValue.trim();
              if (!text) {
                aiResponse =
                  "Please provide text or URL to generate QR code for.";
              } else {
                const qrUrl = await generateQRCodeURL(text);
                // Return structured data for QRCodeGeneratorCard
                aiResponse = JSON.stringify(formatQRCodeData(text, qrUrl));
              }
            } catch (error: any) {
              aiResponse = `❌ QR Code Error: ${error.message}`;
            }
            break;

          case "translator":
            try {
              const patterns = [
                /translate\s*['\"](.*?)['\"]?\s*to\s*(\w+)/i,
                /translate\s*['\"](.*?)['\"]?\s*from\s*\w+\s*to\s*(\w+)/i,
                /translate\s*['\"](.*?)['\"]?\s*in\s*(\w+)/i,
                /(.*?)\s*to\s*(\w+)/i,
              ];
              let text = "";
              let targetLang = "";
              for (const p of patterns) {
                const m = inputValue.match(p);
                if (m) {
                  text = m[1].trim();
                  targetLang = m[2].toLowerCase();
                  break;
                }
              }
              // If no pattern matched, use the entire input as text
              if (!text) {
                text = inputValue.trim();
              }

              const { translated, explanation } = await gptOssTranslatorWithExplanation(text, targetLang);
              aiResponse = JSON.stringify({
                type: 'translate',
                data: { input: text, output: translated, to: targetLang, explanation }
              });
            } catch (error: any) {
              aiResponse = JSON.stringify({ type: 'translate', data: { error: String(error.message || error) } });
            }
            break;

          case "code-explain":
          case "code":
            try {
              const explanation = await gptOssCodeExplain(inputValue);
              aiResponse = explanation;
            } catch (error: any) {
              aiResponse = `❌ Code Explain Error: ${error.message}`;
            }
            break;

          case "color-palette":
            try {
              const paletteResult = await generateColorPaletteWithAI(
                inputValue,
                isDarkMode
              );
              // Store the palette object as JSON string for message content
              aiResponse = JSON.stringify(paletteResult);
            } catch (error) {
              aiResponse = `❌ Color Palette Error: ${error.message}`;
            }
            break;

          case "pixabay-search":
          case "pixabay":
            try {
              const searchQuery = inputValue.trim();
              if (!searchQuery) {
                aiResponse = "Search High Quality Images from Pixabay.";
              } else {
                const pixabayData = await pixabayService(searchQuery);
                aiResponse = formatPixabayResponse(
                  pixabayData,
                  searchQuery,
                  isDarkMode
                );
              }
            } catch (error: any) {
              aiResponse = `❌ Pixabay Search Error: ${error.message}`;
            }
            break;

          case "wiki-images":
            try {
              const searchQuery = inputValue.trim();
              if (!searchQuery) {
                aiResponse = "Search free images from Wikimedia Commons.";
              } else {
                const items = await wikiImagesService(searchQuery);
                aiResponse = formatWikiImagesResponse(items, searchQuery, Boolean(isDarkMode));
              }
            } catch (error: any) {
              aiResponse = `❌ Wiki Images Error: ${error.message}`;
            }
            break;

          case "search":
            try {
              // Import and use the web search service
              const { enhancedWebSearchService, formatSearchResponse } =
                await import("@/services/agents/searchAgent");
              const searchResult = await enhancedWebSearchService(
                inputValue.trim()
              );
              aiResponse = formatSearchResponse(searchResult, isDarkMode);
            } catch (error: any) {
              aiResponse = `❌ Web Search Agent Error: ${error.message}`;
            }
            break;

          case "image-search":
            try {
              // Import and use the Google image search service
              const { googleImageSearch, formatImageSearchResponse } =
                await import("@/services/agents/googleImageSearchAgent");
              const imageResults = await googleImageSearch(inputValue.trim());
              aiResponse = formatImageSearchResponse(imageResults);
            } catch (error: any) {
              aiResponse = `❌ Image Search Agent Error: ${error.message}`;
            }
            break;

          case "SlidesAi": {
            // Generate PPT via Groq and attach structured payload
            const tempId = generateUniqueMessageId();
            const tempAiMessage: Message = {
              id: tempId,
              type: "ai",
              content: "",
              timestamp: new Date(),
              model: "SlidesAi",
              selectedAgent: "SlidesAi",
              isStreaming: true,
            } as any;
            setMessages((prev) => [...prev, tempAiMessage]);

            try {
              const ppt = await generatePPTWithGroq(inputValue.trim());
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === tempId
                    ? {
                      ...m,
                      content: `Generated presentation: ${ppt.title || "AI Presentation"} (${ppt.slides || "?"} slides)`,
                      isStreaming: false,
                      agentData: { ppt },
                    }
                    : m
                )
              );
              if (saveMessageToSupabase) {
                const finalMsg: Message = {
                  id: tempId,
                  type: "ai",
                  content: `Generated presentation: ${ppt.title || "AI Presentation"} (${ppt.slides || "?"} slides)`,
                  timestamp: new Date(),
                  model: "SlidesAi",
                  selectedAgent: "SlidesAi",
                  agentData: { ppt },
                } as any;
                saveMessageToSupabase(finalMsg);
              }
            } catch (error: any) {
              const errMsg: Message = {
                id: generateUniqueMessageId(),
                type: "ai",
                content: `❌ PPT Maker Error: ${error?.message || "Generation failed"}`,
                timestamp: new Date(),
                model: "SlidesAi",
                selectedAgent: "SlidesAi",
              } as any;
              setMessages((prev) => [...prev, errMsg]);
              if (saveMessageToSupabase) saveMessageToSupabase(errMsg);
            } finally {
              setIsLoading(false);
            }
            return;
          }

          case "flow":
          case "flowchart": {
            // Generate flowchart via Groq
            const tempId = generateUniqueMessageId();
            const tempAiMessage: Message = {
              id: tempId,
              type: "ai",
              content: "",
              timestamp: new Date(),
              model: "flow",
              selectedAgent: "flow",
              isStreaming: true,
            } as any;
            setMessages((prev) => [...prev, tempAiMessage]);

            try {
              const flowSpec = await buildFlowChart(inputValue.trim());

              setMessages((prev) =>
                prev.map((m) =>
                  m.id === tempId
                    ? {
                      ...m,
                      content: `📊 Flowchart: ${flowSpec.title}`,
                      isStreaming: false,
                      agentData: { flowchart: flowSpec },
                    }
                    : m
                )
              );

              if (saveMessageToSupabase) {
                const finalMsg: Message = {
                  id: tempId,
                  type: "ai",
                  content: `📊 Flowchart: ${flowSpec.title}`,
                  timestamp: new Date(),
                  model: "flow",
                  selectedAgent: "flow",
                  agentData: { flowchart: flowSpec },
                } as any;
                saveMessageToSupabase(finalMsg);
              }
            } catch (error: any) {
              const errMsg: Message = {
                id: generateUniqueMessageId(),
                type: "ai",
                content: `❌ Flowchart Error: ${error?.message || "Generation failed"}`,
                timestamp: new Date(),
                model: "flow",
                selectedAgent: "flow",
              } as any;
              setMessages((prev) => [...prev, errMsg]);
              if (saveMessageToSupabase) saveMessageToSupabase(errMsg);
            } finally {
              setIsLoading(false);
            }
            return;
          }
        }

        // Create AI message with agent response
        const aiMessage: Message = {
          id: generateUniqueMessageId(),
          type: "ai",
          content:
            selectedAgent?.id === "weather" && weather
              ? (aiResponse || "Weather update")
              : aiResponse,
          timestamp: new Date(),
          model: selectedAgent?.id || selectedModel,
          selectedAgent: selectedAgent?.id,
          ...(selectedAgent?.id === "weather" && weather
            ? { weatherData: weather }
            : {}),
          ...(selectedAgent?.id === "weather" && weather
            ? { agentData: { weatherData: weather } }
            : {}),
        };
        setMessages((prev: Message[]) => [...prev, aiMessage]);
        // Save AI message to Supabase
        if (saveMessageToSupabase) {
          saveMessageToSupabase(aiMessage);
        }
        setInputValue("");
        setIsLoading(false);
        return; // Exit early to prevent duplicate AI model response
      } else {
        try {
          // Single concise persona used for all models
          const fallbackModel = selectedModel || "gpt";
          // System prompt is handled server-side by edge functions

          // Handle ChatGPT with streaming support
          if (
            fallbackModel === "chat gpt" ||
            fallbackModel === "gpt" ||
            selectedModel === "chat gpt" ||
            selectedModel === "gpt"
          ) {
            const tempAiMessageId = generateUniqueMessageId();
            const tempAiMessage: Message = {
              id: tempAiMessageId,
              type: "ai",
              content: "",
              timestamp: new Date(),
              model: fallbackModel,
              isStreaming: true,
            };

            setMessages((prev) => [...prev, tempAiMessage]);

            try {
              let buffer = "";
              let reasoningBuffer = "";
              let executedToolsBuffer: any[] = [];
              let lastUpdate = 0;
              let animationFrameId: number | null = null;
              let hasStructuredContent = false;
              let updateCount = 0;


              // Optimized update function with smart throttling
              const updateMessage = (final = false) => {
                if (animationFrameId && !final) return;

                animationFrameId = requestAnimationFrame(() => {
                  let displayContent = buffer;

                  // Basic cleanup: remove <think> blocks
                  displayContent = displayContent.replace(/<think>[\s\S]*?<\/think>/gi, '').trim();

                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === tempAiMessageId
                        ? {
                          ...msg,
                          content: displayContent || buffer, // Fallback to buffer if sanitization clears everything (unlikely)
                          isStreaming: !final,
                        }
                        : msg
                    )
                  );
                  animationFrameId = null;
                  lastUpdate = performance.now();
                  updateCount++;
                });
              };

              // Call ChatGPT Supabase function with streaming using proper URL and auth
              const SUPABASE_URL = "https://arikhtozvkvxlraiuenh.supabase.co";
              const { data: authData } = await supabase.auth.getSession();

              const headers: Record<string, string> = {
                "Content-Type": "application/json",
              };
              if (authData?.session?.access_token) {
                headers[
                  "Authorization"
                ] = `Bearer ${authData.session.access_token}`;
              } else {
                // Fallback to anon key only if no session (public access)
                headers[
                  "Authorization"
                ] = "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWtodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU";
              }

              const response = await fetch(
                `${SUPABASE_URL}/functions/v1/chatgpt_v2`,
                {
                  method: "POST",
                  headers,
                  body: JSON.stringify({
                    messages: [
                      { role: "user", content: enrichPromptForFileCreation(inputWithContext) },
                    ],
                    stream: true,
                    enable_web_search: true,
                    enable_code_execution: true,
                    reasoning_effort: "medium",
                  }),
                }
              );

              if (!response.ok) {
                const errorText = await response.text();
                console.error(`[ChatGPT] HTTP Error ${response.status}:`, errorText);
                throw new Error(
                  `HTTP ${response.status}: ${response.statusText}`
                );
              }

              if (response.body) {
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let streamBuffer = "";

                try {
                  while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    streamBuffer += decoder.decode(value, { stream: true });

                    const lines = streamBuffer.split("\n");
                    streamBuffer = lines.pop() || "";

                    for (const rawLine of lines) {
                      const line = rawLine.trim();
                      if (!line || line === "data: [DONE]") continue;

                      if (line.startsWith("data: ")) {
                        try {
                          const data = JSON.parse(line.slice(6));
                          const delta = data.choices[0]?.delta || {};
                          const chunk = delta.content || "";
                          const reasoningChunk = delta.reasoning_content || "";
                          const newExecutedTools = data.executed_tools || [];

                          // Accumulate content
                          if (chunk) {
                            buffer += chunk;
                          }

                          // Accumulate reasoning
                          if (reasoningChunk) {
                            reasoningBuffer += reasoningChunk;
                          }

                          // Accumulate executed tools
                          if (newExecutedTools.length > 0) {
                            executedToolsBuffer = [...executedToolsBuffer, ...newExecutedTools];
                          }

                          if (chunk) {
                            if (
                              !hasStructuredContent &&
                              (chunk.includes("```") || chunk.includes("|"))
                            ) {
                              hasStructuredContent = true;
                            }

                            const now = performance.now();
                            const timeSinceLastUpdate = now - lastUpdate;

                            if (hasStructuredContent) {
                              if (timeSinceLastUpdate > 50) {
                                updateMessage();
                              } else if (!animationFrameId && updateCount < 5) {
                                updateMessage();
                              }
                            } else {
                              if (timeSinceLastUpdate > 8) {
                                updateMessage();
                              } else if (!animationFrameId) {
                                updateMessage();
                              }
                            }
                          }
                        } catch (e) {
                          console.error("[ChatGPT] Error parsing chunk:", e, "Line:", line);
                        }
                      }
                    }
                  }
                } catch (e) {
                  console.error("[ChatGPT] Stream reading error:", e);
                  throw e;
                }
              }

              // Final update
              if (buffer) {
                updateMessage(true);

                // Update message with reasoning and executed tools
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === tempAiMessageId
                      ? {
                        ...msg,
                        reasoning_content: reasoningBuffer || undefined,
                        executedTools: executedToolsBuffer.length > 0 ? executedToolsBuffer : undefined,
                      }
                      : msg
                  )
                );

                // Save completed AI message to Supabase
                if (saveMessageToSupabase) {
                  const completedMessage = {
                    id: tempAiMessageId,
                    type: "ai",
                    content: buffer,
                    timestamp: new Date(),
                    model: fallbackModel,
                    isStreaming: false,
                    reasoning_content: reasoningBuffer || undefined,
                    executedTools: executedToolsBuffer.length > 0 ? executedToolsBuffer : undefined,
                  };
                  saveMessageToSupabase(completedMessage);
                }
              }

              setIsLoading(false);
              return;
            } catch (error: any) {
              console.error("Error in ChatGPT streaming:", error);
              // Show error directly - no fallback
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempAiMessageId
                    ? {
                      ...msg,
                      content: `❌ Error getting response from ChatGPT: ${error.message || 'Please try again.'}`,
                      isStreaming: false,
                    }
                    : msg
                )
              );
              setIsLoading(false);
              return;
            }
          } else if (selectedModel === "gpt5") {
            // Handle GPT-5 with streaming support
            const tempAiMessageId = generateUniqueMessageId();
            const tempAiMessage: Message = {
              id: tempAiMessageId,
              type: "ai",
              content: "",
              timestamp: new Date(),
              model: selectedModel,
              isStreaming: true,
            };

            setMessages((prev) => [...prev, tempAiMessage]);

            try {
              let fullResponse = "";

              await aiChatService(
                selectedModel,
                [
                  { role: "user", content: inputWithContext },
                ],
                0.7,
                (chunk: string) => {
                  if (chunk && fullResponse && chunk.startsWith(fullResponse)) {
                    fullResponse = chunk; // cumulative
                  } else {
                    fullResponse += chunk; // delta
                  }
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === tempAiMessageId
                        ? { ...msg, content: fullResponse, isStreaming: true }
                        : msg
                    )
                  );
                }
              );

              // Mark streaming as complete
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempAiMessageId
                    ? { ...msg, isStreaming: false }
                    : msg
                )
              );

              // Memory saving removed

              // Save completed AI message to Supabase
              if (saveMessageToSupabase) {
                const completedMessage = {
                  id: tempAiMessageId,
                  type: "ai",
                  content: fullResponse,
                  timestamp: new Date(),
                  model: selectedModel,
                  isStreaming: false,
                };
                saveMessageToSupabase(completedMessage);
              }

              setIsLoading(false);
              return;
            } catch (error: any) {
              console.error("Error in GPT-5 streaming:", error);
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === tempAiMessageId
                    ? {
                      ...msg,
                      content: `❌ Error getting response from GPT-5: ${error.message}`,
                      isStreaming: false,
                    }
                    : msg
                )
              );
              setIsLoading(false);
              return;
            }

            // (Removed duplicate sources handling block here; main handling occurs later in non-GPT5 flow)
          }

          // Generic single-model streaming handler for providers not explicitly handled above
          // Covers: deepseek (r1/v3), gemini, llama, and other aiChatService-supported ids
          const lowerModel = (selectedModel || '').toLowerCase();
          const isExplicitlyHandled = (
            lowerModel === 'gpt-oss' ||
            lowerModel === 'qwen' ||
            lowerModel === 'sambanova' ||
            lowerModel === 'kimi' ||
            lowerModel === 'perplexity' ||
            lowerModel === 'cerebras' ||
            lowerModel === 'upstage' ||
            lowerModel === 'chat gpt' ||
            lowerModel === 'gpt' ||
            lowerModel === 'gpt5' ||
            lowerModel === 'google ai' ||
            lowerModel === 'googleai' ||
            lowerModel === 'lara'
          );
          if (!isExplicitlyHandled) {
            const tempAiMessageId = generateUniqueMessageId();
            const tempAiMessage: Message = {
              id: tempAiMessageId,
              type: 'ai',
              content: '',
              timestamp: new Date(),
              model: selectedModel,
              isStreaming: true,
            } as any;
            setMessages((prev) => [...prev, tempAiMessage]);

            try {
              let full = '';
              const toServiceId = (id: string): string => {
                const k = (id || '').toLowerCase();
                if (k === 'chat gpt' || k === 'chatgpt' || k === 'gpt') return 'glm';
                if (k === 'solar pro') return 'upstage';
                if (k === 'gpt 5') return 'gpt5';
                return k; // deepseek, gemini, llama, etc.
              };
              const serviceId = toServiceId(selectedModel);
              // System prompt is handled server-side by edge functions
              await aiChatService(
                serviceId,
                [
                  { role: 'user', content: inputWithContext },
                ],
                0.7,
                (chunk: string) => {
                  if (chunk && full && chunk.startsWith(full)) {
                    full = chunk; // cumulative
                  } else {
                    full += chunk; // delta
                  }
                  setMessages((prev) =>
                    prev.map((m) =>
                      m.id === tempAiMessageId ? { ...m, content: full, isStreaming: true } : m
                    )
                  );
                },
                (reasoning: string) => {
                  // Live reasoning for Gemini in generic streaming path
                  if ((selectedModel || '').toLowerCase() === 'gemini') {
                    setMessages((prev) =>
                      prev.map((m) => (m.id === tempAiMessageId ? { ...m, reasoning } : m))
                    );
                  }
                }
              );

              setMessages((prev) =>
                prev.map((m) => (
                  m.id === tempAiMessageId
                    ? {
                      ...m,
                      isStreaming: false,
                      ...(((selectedModel || '').toLowerCase() === 'gemini' && lastGeminiReasoning)
                        ? { reasoning: lastGeminiReasoning }
                        : {}),
                    }
                    : m
                ))
              );

              if (saveMessageToSupabase) {
                const finalMsg: Message = {
                  id: tempAiMessageId,
                  type: 'ai',
                  content: full,
                  timestamp: new Date(),
                  model: selectedModel,
                  isStreaming: false,
                  ...(((selectedModel || '').toLowerCase() === 'gemini' && lastGeminiReasoning)
                    ? { reasoning: lastGeminiReasoning }
                    : {}),
                } as any;
                saveMessageToSupabase(finalMsg);
              }

              setIsLoading(false);
              return;
            } catch (error: any) {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === tempAiMessageId
                    ? { ...m, content: `❌ Error: ${error?.message || 'Failed to get response'}`, isStreaming: false }
                    : m
                )
              );
              setIsLoading(false);
              return;
            }
          }
        } catch (error: any) {
          aiResponse = `❌ AI Error: ${error.message}`;
        }
      }

      // Single-AI: Google AI (SerpAPI Google AI Mode) with streaming + sources attach
      if ((selectedModel || '').toLowerCase() === 'google ai' || (selectedModel || '').toLowerCase() === 'googleai') {
        const tempAiMessageId = generateUniqueMessageId();
        const tempAiMessage: Message = {
          id: tempAiMessageId,
          type: 'ai',
          content: '',
          timestamp: new Date(),
          model: selectedModel,
          isStreaming: true,
        };
        setMessages((prev) => [...prev, tempAiMessage]);

        try {
          let buffer = '';
          const result: any = await aiChatService(
            'google ai',
            [{ role: 'user', content: inputValue }],
            0.7,
            (chunk: string) => {
              buffer += chunk;
              setMessages((prev) =>
                prev.map((msg) => (msg.id === tempAiMessageId ? { ...msg, content: buffer, isStreaming: true } : msg))
              );
            }
          );

          const googleSources: Array<{ title?: string; url?: string; snippet?: string; favicon?: string }> = (result as any)?.sources || [];
          const links: { title: string; url: string; snippet?: string }[] = [];
          if (Array.isArray(googleSources) && googleSources.length) {
            for (const r of googleSources) {
              const url = (r as any).url || (r as any).link || '';
              if (url) links.push({ title: (r as any).title || url, url, snippet: (r as any).snippet });
            }
          }
          const finalSearchResults = links.length
            ? ({
              query: inputValue,
              answer: (result as any)?.text || (result as any)?.response || '',
              results: links.map((l) => ({ title: l.title, url: l.url, snippet: l.snippet })),
              gptEnhanced: true,
            } as any)
            : undefined;

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempAiMessageId
                ? {
                  ...msg,
                  content: result.content || buffer,
                  isStreaming: false,
                  ...(finalSearchResults ? { searchResults: finalSearchResults } : {}),
                }
                : msg
            )
          );

          if (saveMessageToSupabase) {
            const completed: Message = {
              id: tempAiMessageId,
              type: 'ai',
              content: result.content || buffer,
              timestamp: new Date(),
              model: selectedModel,
              ...(finalSearchResults ? { searchResults: finalSearchResults } : {}),
            };
            saveMessageToSupabase(completed);
          }

          setIsLoading(false);
          return;
        } catch (error: any) {
          console.error('Google AI error:', error);
          setMessages((prev) =>
            prev.map((msg) => (msg.id === tempAiMessageId ? { ...msg, content: `❌ Error: ${error.message}`, isStreaming: false } : msg))
          );
          setIsLoading(false);
          return;
        }
      }

      // Create AI message with response
      const aiMessage: Message = {
        id: generateUniqueMessageId(),
        type: "ai",
        content: aiResponse || "No response received",
        timestamp: new Date(),
        model: selectedModel,
        reasoning_content: reasoningContent,
        ...(finalSearchResults ? { searchResults: finalSearchResults as any } : {}),
      };
      setMessages((prev: Message[]) => [...prev, aiMessage]);
      // Save AI message to Supabase
      if (saveMessageToSupabase) {
        saveMessageToSupabase(aiMessage);
      }

      // Save to chat memory for non-agent responses
      if (
        aiResponse &&
        typeof aiResponse === "string" &&
        !aiResponse.includes("❌")
      ) {
        // Memory saving removed
      }

      setAttachedMedia(null);
      setSelectedMemeTemplate(null);
      setShowMemeTemplates(false);
      setGifs([]);
      setShowGifs(false);
    } catch (error: any) {
      console.error("API request failed:", error);
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });

      const aiMessage: Message = {
        id: generateUniqueMessageId(),
        type: "ai",
        content:
          "I'm having trouble processing your request right now. Please try again in a moment.",
        timestamp: new Date(),
        model: selectedModel,
      };
      setMessages((prev: Message[]) => [...prev, aiMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  return { handleSend };
};
